// ═══════════════════════════════════════════════════════════════════
// TaskForm.jsx — Formulaire tâche + planning semaine + helper CA
// Composants : TaskForm, WeekPlan
// Helpers exportés : caTask (utilisé aussi par SecRH/VueGen, Script 6)
// ═══════════════════════════════════════════════════════════════════
import React from 'react';
import { notifEventBus } from "../notifBus.js";
import { M } from './M.jsx'
import { Ic } from './Ic.jsx'
import { TT, HSTART, HEND, HTOT, EVIIVO_HEURES } from '../constants.js'
import {
  today, now, fd, feur, fname, fnameShort, effSt, gMon, aD, iD,
  montantInterventionHT, dureeFacturableIntervention, rhHContratJours, rhHAbsJours,
  contratActif, dernierContrat, travailPossibleJusqua,
} from '../utils.js'
import { supaGet, supaSet } from '../supabase.js'
import { absencesPourPlanning } from '../absencesPlanning.js'

const { useState, useEffect, useMemo, Fragment} = React

// Tâches "ménage" au sens rapport de fin de prestation (même définition que GerantUI.jsx/GerantClientUI.jsx)
const isMenageType=type=>['full','quick','linge'].includes(type);

// ══════════════ FORMULAIRE CRÉATION / ÉDITION TÂCHE ══════════════
function TaskForm({prefill,biens,staff,prov,st,pj,clients,onSave,onC,onDelete,onDuplicate}){
  const _clients=Array.isArray(clients)?clients:[];
  const[saving,setSaving]=useState(false);
  const isStaff=prefill?.assigneeType!=='provider';
  // En édition (prefill.id existe), on respecte sid/pid même null pour garder le statut "non affecté"
  // En création (pas d'id), on pré-sélectionne le premier salarié/prestataire pour l'UX
  const isEdit=!!prefill?.id;
  // Durée par défaut : 15 min pour check-in/check-out (saisie rapide), 60 min sinon
  const dmDefault=(prefill?.type==='checkin'||prefill?.type==='checkout')?15:60;
  // Helper : calcule l'heure de début proposée selon le type de tâche et les tâches existantes du salarié
  // - Heures planchers : ménage/linge=10h, check-in=16h, check-out=9h
  // - Si la personne a déjà des tâches ce jour-là, on cale juste après la dernière (en respectant le plancher)
  // - excludeId : pour exclure la tâche en cours d'édition du calcul
  const computeStartHour=(type,sid,date,excludeId)=>{
    let floor=10*60; // par défaut 10h pour full/quick/linge
    if(type==='checkin'||type==='gardeCheckin') floor=16*60;
    else if(type==='checkout') floor=9*60;
    if(!sid||!date) return{sh:Math.floor(floor/60),sm:floor%60};
    // Cherche la fin de la dernière tâche du salarié ce jour-là
    const lastEnd=st.filter(t=>t.sid===sid&&t.date===date&&t.id!==excludeId)
      .reduce((max,t)=>Math.max(max,t.sh*60+t.sm+t.dm),0);
    const start=Math.max(floor,lastEnd);
    return{sh:Math.floor(start/60),sm:start%60};
  };
  // Calcul initial : en création, on applique la logique. En édition, on respecte ce qui a été saisi.
  const initType=prefill?.type||'full';
  const initSid=isEdit?(prefill?.sid??''):(prefill?.sid===null?'':(prefill?.sid||staff[0]?.id||''));
  const initBid=TT[initType]?.noBid?'':(prefill?.bid||biens[0]?.id||'');
  const initDate=prefill?.date||today();
  // Binôme : si on édite une tâche déjà liée, on retrouve la tâche sœur pour pré-remplir
  // le formulaire (staff seulement — un binôme n'a de sens que côté salarié).
  const siblingBinome=prefill?.groupeBinome?st.find(t=>t.groupeBinome===prefill.groupeBinome&&t.id!==prefill.id):null;
  const initHour=isEdit
    ?{sh:prefill?.sh??9,sm:prefill?.sm??0}
    :(prefill?.sh!==undefined?{sh:prefill.sh,sm:prefill.sm??0}:computeStartHour(initType,initSid,initDate,null));
  // Durée initiale : si prefill.dm fourni on le respecte. Sinon on calcule depuis le bien pré-sélectionné + type.
  // Pour les check-in/check-out, la durée par défaut (15min) prime sur la durée du bien.
  let initDm=prefill?.dm;
  if(initDm===undefined){
    if(initType==='checkin'||initType==='checkout') initDm=15;
    else{
      const b=biens.find(x=>x.id===initBid);
      if(b) initDm=initType==='full'?Number(b.dmFull||0):initType==='quick'?Number(b.dmQuick||0):initType==='linge'?Number(b.dmLinge||0):0;
      if(!initDm||initDm<=0) initDm=60;
    }
  }
  const[f,setF]=useState({
    assigneeType: prefill?.assigneeType||'staff',
    sid: initSid,
    pid: isEdit?(prefill?.pid??''):(prefill?.pid||''),
    bid: initBid,
    date: initDate,
    type: initType,
    sh: initHour.sh, sm: initHour.sm, dm: initDm,
    notes: prefill?.notes||'',
    alerte: prefill?.alerte||'', // consigne "attention" plein écran, distincte de notes (v145)
    prio: prefill?.prio||'normal',
    af: prefill?.af||'09:00', db: prefill?.db||'17:00',
    st: prefill?.st||'todo',
    surcharge25: prefill?.surcharge25||false,
    risque: prefill?.risque||false,
    applyTarifUrgent: prefill?.applyTarifUrgent||false,
    noReport: prefill?.noReport||false, // si true, le rapport de ménage n'est pas obligatoire pour cette tâche (cas par cas, décidé par admin)
    // Facturation prestations-semaine (Morceau 1) : tags posés par l'admin.
    // semaineFacture = lundi (YYYY-MM-DD) de la semaine de facturation ; jourSortie = date de sortie réelle (YYYY-MM-DD).
    semaineFacture: prefill?.semaineFacture||'',
    jourSortie: prefill?.jourSortie||'',
    rdv: prefill?.rdv||false, // check-in : true = l'heure est le rendez-vous confirmé avec le client (pas le défaut 16:00)
    autoCheckin: false, // toujours false à l'ouverture : c'est une intention de création, pas une donnée persistée
    // Binôme (staff uniquement) : groupeBinome/binomeRole sont les champs persistés sur la tâche.
    // binome/binomeSid/binomeRapport sont des champs de PILOTAGE DU FORMULAIRE, jamais persistés tels quels.
    groupeBinome: prefill?.groupeBinome||null,
    binomeRole: prefill?.binomeRole||null, // 'titulaire' | 'binome' | null
    binome: prefill?.binomeRole==='titulaire', // coché seulement côté titulaire ; côté binome on affiche un état lecture seule
    binomeSid: siblingBinome?.sid||'',
    // Qui remplit le rapport de fin de ménage : déduit de noReport de chaque côté (le responsable a noReport=false)
    binomeRapport: prefill?.binomeRole==='titulaire'
      ? (prefill?.noReport?'binome':'titulaire')
      : (siblingBinome?(siblingBinome.noReport?'titulaire':'binome'):'titulaire'),
    id: prefill?.id||null,
    // Flag interne : passe à true dès que l'utilisateur modifie manuellement l'heure
    _hourTouched: isEdit, // en édition, l'heure est considérée comme déjà saisie
  });
  const set=(k,v)=>setF(x=>({...x,[k]:v}));
  // Marque l'heure comme manuellement modifiée pour ne plus la recalculer automatiquement
  const setHour=(sh,sm)=>setF(x=>({...x,sh,sm,_hourTouched:true}));
  const isP=f.assigneeType==='provider';
  // Types "sans appartement" (garde check-in, boutique) : pas de bid, pas de conflit horaire
  // avec les vraies tâches qui viendront se loger dans le créneau (c'est le but recherché).
  const noApt=!isP&&!!TT[f.type]?.noBid;

  // Mini-planning du jour : tâches existantes du salarié/prestataire sélectionné ce jour-là
  const dayTasks=useMemo(()=>{
    if(!f.date) return [];
    if(!isP&&f.sid){
      return st.filter(t=>t.sid===f.sid&&t.date===f.date&&t.id!==f.id)
        .map(t=>({start:t.sh*60+t.sm,end:t.sh*60+t.sm+t.dm,label:TT[t.type]?.noBid?TT[t.type].l:(biens.find(b=>b.id===t.bid)?.name||'?'),type:t.type,isCurrent:false,isGarde:!!TT[t.type]?.noBid}));
    }
    if(isP&&f.pid){
      return pj.filter(j=>j.pid===f.pid&&j.date===f.date&&j.id!==f.id)
        .map(j=>{const[ah,am]=(j.af||'09:00').split(':').map(Number);const[bh,bm]=(j.db||'17:00').split(':').map(Number);return{start:ah*60+am,end:bh*60+bm,label:biens.find(b=>b.id===j.bid)?.name||'?',type:'provider',isCurrent:false,isGarde:false};});
    }
    return [];
  },[f.sid,f.pid,f.date,f.assigneeType,st,pj]);
  // Pour la détection de chevauchement : on ignore les créneaux de garde (sans appartement),
  // qui sont justement faits pour accueillir les vraies tâches check-in une fois connues.
  const dayTasksForConflict=useMemo(()=>dayTasks.filter(t=>!t.isGarde),[dayTasks]);

  // Créneau en cours de création
  const currentSlot=!isP?{start:f.sh*60+(f.sm||0),end:f.sh*60+(f.sm||0)+(f.dm||60),isCurrent:true}:null;


  // Calcul tarif prestataire en temps réel
  const tarifPrest=useMemo(()=>{
    if(!isP||!f.pid||!f.bid) return null;
    const p=prov.find(x=>x.id===f.pid);
    if(!p) return null;
    if(p.tarifType==='forfait'){
      const type=f.type||'full';
      if(type==='checkin') return{label:'Forfait check-in',montant:p.tarifCheckin||0,mode:'forfait'};
      if(type==='checkout') return{label:'Forfait check-out',montant:p.tarifCheckout||0,mode:'forfait'};
      // ménage → forfait par appartement
      const m=(p.forfaits||{})[f.bid];
      if(m!=null) return{label:'Forfait ménage',montant:m,mode:'forfait'};
      return{label:'Forfait non défini',montant:null,mode:'forfait'};
    }
    if(p.tarifType==='horaire'){
      const h=(f.dm||90)/60;
      return{label:`Taux horaire × ${h.toFixed(1)}h`,montant:Math.round(p.tauxHoraire*h*100)/100,mode:'horaire',taux:p.tauxHoraire};
    }
    return null;
  },[f.pid,f.bid,f.type,f.dm,isP,prov]);
  const pct=min=>Math.max(0,Math.min(100,((min-HSTART*60)/HTOT)*100));
  const hasSchedule=(dayTasks.length>0||currentSlot)&&f.date;
  const assigneeName=!isP?(staff.find(s=>s.id===f.sid)?.firstName||staff.find(s=>s.id===f.sid)?.name?.split(' ')[0]||''):prov.find(p=>p.id===f.pid)?.name?.split(' ')[0];

  return(<div style={{display:'flex',flexDirection:'column',gap:14}}>
    {/* Qui */}
    <div>
      <label className="fl">Type d'intervenant</label>
      <div style={{display:'flex',gap:8,marginBottom:10}}>
        {[['staff','👷 Salarié','var(--mn)'],['provider','🔧 Prestataire','var(--or)']].map(([v,l,c])=>(
          <button key={v} onClick={()=>set('assigneeType',v)} className={`btn sm ${f.assigneeType===v?'':'bgh'}`} style={f.assigneeType===v?{background:c,color:'white',border:`1.5px solid ${c}`}:{}}>{l}</button>
        ))}
      </div>
      {!isP
        ?<>
          <select value={f.sid} onChange={e=>{
            const sid=e.target.value;
            setF(x=>{
              const next={...x,sid};
              // Recalcul auto seulement si l'utilisateur n'a pas touché à l'heure
              if(!x._hourTouched){
                const h=computeStartHour(x.type,sid,x.date,x.id);
                next.sh=h.sh; next.sm=h.sm;
              }
              return next;
            });
          }}>
            <option value="">— Non affecté —</option>
            {staff.filter(s=>!s.archived).map(s=><option key={s.id} value={s.id}>{fname(s)} · {s.role}</option>)}
          </select>
          {/* ─── Binôme (staff uniquement) ───
              Côté titulaire (ou tâche normale) : réglage complet.
              Côté tâche "binôme" (l'autre moitié) : état lecture seule + dissociation. */}
          {f.sid&&f.binomeRole!=='binome'&&(
            <div style={{marginTop:8}}>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,cursor:'pointer'}}>
                <input type="checkbox" checked={f.binome} onChange={e=>set('binome',e.target.checked)}/>
                🤝 Tâche en binôme
              </label>
              {f.binome&&<div style={{marginTop:6,display:'flex',flexDirection:'column',gap:6,paddingLeft:2}}>
                <select value={f.binomeSid} onChange={e=>set('binomeSid',e.target.value)}>
                  <option value="">— Choisir le binôme —</option>
                  {staff.filter(s=>!s.archived&&s.id!==f.sid).map(s=><option key={s.id} value={s.id}>{fname(s)} · {s.role}</option>)}
                </select>
                {f.binomeSid&&isMenageType(f.type)&&<select value={f.binomeRapport} onChange={e=>set('binomeRapport',e.target.value)}>
                  <option value="titulaire">📝 Rapport rempli par {fname(staff.find(s=>s.id===f.sid))||'moi'}</option>
                  <option value="binome">📝 Rapport rempli par {fname(staff.find(s=>s.id===f.binomeSid))||'le binôme'}</option>
                </select>}
              </div>}
            </div>
          )}
          {f.binomeRole==='binome'&&(
            <div style={{marginTop:8,fontSize:12,color:'var(--si)',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span>🤝 Tâche en binôme (liée à une autre tâche)</span>
              <button type="button" onClick={()=>set('_dissocierBinome',true)} className="btn bgh sm" style={{fontSize:11}}>Dissocier</button>
              {f._dissocierBinome&&<span style={{color:'var(--dg)',fontWeight:600}}>✓ sera dissociée à l'enregistrement</span>}
            </div>
          )}
        </>
        :<div>
          <select value={f.pid} onChange={e=>set('pid',e.target.value)}>
            <option value="">— Non affecté —</option>
            {prov.filter(p=>p.active).map(p=>{
              // Calcul tarif pour cet appart + type de tâche
              let tarif=null;
              if(p.tarifType==='forfait'){
                const type=f.type||'full';
                if(type==='checkin') tarif=p.tarifCheckin||0;
                else if(type==='checkout') tarif=p.tarifCheckout||0;
                else tarif=(p.forfaits||{})[f.bid]||null;
              } else if(p.tarifType==='horaire'){
                tarif=Math.round(p.tauxHoraire*(f.dm||90)/60*100)/100;
              }
              return <option key={p.id} value={p.id}>{p.name} · {p.type}{tarif!=null?' · '+feur(tarif):''}</option>;
            })}
          </select>
          {/* Indication visuelle du moins cher */}
          {f.bid&&(()=>{
            const tarifsPrest=prov.filter(p=>p.active).map(p=>{
              let t=null;
              if(p.tarifType==='forfait'){
                const type=f.type||'full';
                if(type==='checkin') t=p.tarifCheckin||0;
                else if(type==='checkout') t=p.tarifCheckout||0;
                else t=(p.forfaits||{})[f.bid]||null;
              } else if(p.tarifType==='horaire') t=Math.round(p.tauxHoraire*(f.dm||90)/60*100)/100;
              return{pid:p.id,name:p.name,tarif:t};
            }).filter(x=>x.tarif!=null).sort((a,b)=>a.tarif-b.tarif);
            if(tarifsPrest.length<2) return null;
            const moins=tarifsPrest[0];
            return(<div style={{marginTop:6,fontSize:11,background:'#e1f7ee',border:'1px solid var(--mn)',borderRadius:7,padding:'5px 10px',color:'#1e6e3d',fontWeight:600}}>
              💚 Le moins cher : {moins.name} — {feur(moins.tarif)}
            </div>);
          })()}
        </div>
      }
      {/* Affichage tarif prestataire */}
      {isP&&f.pid&&f.bid&&tarifPrest&&(
        <div style={{marginTop:8,padding:'10px 14px',borderRadius:10,background:tarifPrest.montant!=null?'#e1f7ee':'#fff4d6',border:`1.5px solid ${tarifPrest.montant!=null?'var(--mn)':'var(--ye)'}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--si)',marginBottom:2}}>{tarifPrest.label}</div>
            {tarifPrest.mode==='horaire'&&<div style={{fontSize:10,color:'var(--si)'}}>{tarifPrest.taux} €/h × {((f.dm||90)/60).toFixed(1)}h</div>}
            {tarifPrest.montant==null&&<div style={{fontSize:11,color:'var(--ol)'}}>⚠ Aucun forfait défini pour cet appartement</div>}
          </div>
          {tarifPrest.montant!=null&&<div style={{fontFamily:'Fredoka,cursive',fontSize:22,color:'var(--ok)',fontWeight:700}}>{feur(tarifPrest.montant)}</div>}
          {tarifPrest.montant==null&&<div style={{fontSize:11,color:'var(--or)',fontWeight:700}}>À définir</div>}
        </div>
      )}
      {isP&&f.pid&&!f.bid&&<div style={{marginTop:6,fontSize:11,color:'var(--si)',fontStyle:'italic'}}>💡 Sélectionnez un appartement pour voir le tarif applicable.</div>}
    </div>

    {/* Bien + date */}
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
      {!noApt?(
        <div style={{gridColumn:'1/-1'}}>
          <label className="fl">Appartement *</label>
          <select value={f.bid} onChange={e=>{
            const bid=e.target.value;
            const b=biens.find(x=>x.id===bid);
            const dmDefault=b?(f.type==='full'?Number(b.dmFull||0):f.type==='quick'?Number(b.dmQuick||0):f.type==='linge'?Number(b.dmLinge||0):0):0;
            if(dmDefault>0){setF(x=>({...x,bid,dm:dmDefault}));}else{set('bid',bid);}
          }}>
            <option value="">— Sélectionner —</option>
            {biens.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      ):(
        <div style={{gridColumn:'1/-1',fontSize:11,color:'var(--si)',fontStyle:'italic',background:'var(--bg)',borderRadius:8,padding:'8px 10px',border:'1px solid var(--li)'}}>
          {TT[f.type]?.e} Poste sans appartement — réserve juste le créneau sur le planning. Les vraies tâches check-in (facturables, pointables) seront créées séparément dès que tu connaîtras le détail.
        </div>
      )}
      <div>
        <label className="fl">Date *</label>
        <input type="date" value={f.date} onChange={e=>{
          const date=e.target.value;
          setF(x=>{
            const next={...x,date};
            if(!x._hourTouched){
              const h=computeStartHour(x.type,x.sid,date,x.id);
              next.sh=h.sh; next.sm=h.sm;
            }
            return next;
          });
        }}/>
      </div>
      {!isP&&<div>
        <label className="fl">Type de tâche</label>
        <select value={f.type} onChange={e=>{
          const type=e.target.value;
          const becomesNoApt=!!TT[type]?.noBid;
          const b=biens.find(x=>x.id===f.bid);
          // Check-in/check-out : 15 min par défaut. Sinon : durée du bien selon le type.
          let dmDefault=0;
          if(becomesNoApt) dmDefault=240; // 4h par défaut pour un poste de garde, ajustable via "Heure fin"
          else if(type==='checkin'||type==='checkout') dmDefault=15;
          else if(b) dmDefault=type==='full'?Number(b.dmFull||0):type==='quick'?Number(b.dmQuick||0):type==='linge'?Number(b.dmLinge||0):0;
          setF(x=>{
            const next={...x,type};
            // Sécurité : jamais de bid sur un poste sans appartement (sinon caTask le facturerait à tort)
            if(becomesNoApt) next.bid='';
            if(dmDefault>0) next.dm=dmDefault;
            // Recalculer l'heure si pas touchée manuellement (et pas en édition)
            if(!x._hourTouched){
              const h=computeStartHour(type,x.sid,x.date,x.id);
              next.sh=h.sh; next.sm=h.sm;
            }
            return next;
          });
        }}>
          {Object.entries(TT).map(([k,v])=><option key={k} value={k}>{v.e} {v.l}</option>)}
        </select>
      </div>}
    </div>

    {/* ── Mini-planning du jour ── */}
    {hasSchedule&&assigneeName&&(<div style={{background:'var(--bg)',borderRadius:10,padding:'10px 12px',border:'1px solid var(--li)'}}>
      <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--si)',marginBottom:8}}>
        Planning de {assigneeName} — {f.date?new Date(f.date+'T12:00').toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'short'}):''}
      </div>
      {/* Frise horaire */}
      <div style={{position:'relative',height:44,background:'var(--wh)',borderRadius:8,border:'1px solid var(--li)',overflow:'hidden'}}>
        {/* Graduations heures */}
        {Array.from({length:HEND-HSTART+1},(_,i)=>i).map(i=>(
          <div key={i} style={{position:'absolute',left:`${(i/(HEND-HSTART))*100}%`,top:0,bottom:0,borderLeft:i>0?'1px solid rgba(42,37,32,.08)':undefined,zIndex:0}}>
            {i>0&&i<HEND-HSTART&&<span style={{position:'absolute',top:2,left:2,fontSize:9,color:'rgba(42,37,32,.45)',fontWeight:600}}>{HSTART+i}h</span>}
          </div>
        ))}
        {/* Tâches existantes */}
        {dayTasks.map((t,i)=>{
          const tt=TT[t.type]||TT.full;
          const left=pct(t.start),width=Math.max(1,pct(t.end)-pct(t.start));
          return(<div key={i} style={{position:'absolute',top:6,height:32,left:`${left}%`,width:`${width}%`,background:tt.bg||'#e0e0e0',borderLeft:`3px solid ${tt.c||'#aaa'}`,borderRadius:'0 5px 5px 0',display:'flex',alignItems:'center',overflow:'hidden',zIndex:1}}>
            <span style={{fontSize:10,fontWeight:700,color:tt.c,paddingLeft:4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{t.label}</span>
          </div>);
        })}
        {/* Créneau en cours */}
        {currentSlot&&(()=>{
          const left=pct(currentSlot.start),width=Math.max(1,pct(currentSlot.end)-pct(currentSlot.start));
          const overlaps=dayTasksForConflict.some(t=>currentSlot.start<t.end&&currentSlot.end>t.start);
          return(<div style={{position:'absolute',top:3,height:38,left:`${left}%`,width:`${width}%`,background:overlaps?'rgba(232,69,69,.35)':'rgba(255,134,23,.35)',border:`2px solid ${overlaps?'var(--dg)':'var(--or)'}`,borderRadius:5,zIndex:2,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <span style={{fontSize:10,fontWeight:800,color:overlaps?'var(--dg)':'var(--or)',whiteSpace:'nowrap'}}>{overlaps?'⚠ Conflit':'✦ Nouveau'}</span>
          </div>);
        })()}
      </div>
      {/* Légende horaire */}
      <div style={{display:'flex',justifyContent:'space-between',marginTop:3,paddingLeft:0,paddingRight:2}}>
        {[7,9,11,13,15,17,19,21].map(h=><span key={h} style={{fontSize:8,color:'rgba(42,37,32,.35)',fontWeight:600}}>{h}h</span>)}
      </div>
      {/* Détail des tâches du jour */}
      {dayTasks.length>0&&<div style={{marginTop:8,display:'flex',flexDirection:'column',gap:3}}>
        {dayTasks.map((t,i)=>{
          const tt=TT[t.type]||TT.full;
          const sh=Math.floor(t.start/60),sm=t.start%60,eh=Math.floor(t.end/60),em=t.end%60;
          return(<div key={i} style={{display:'flex',alignItems:'center',gap:6,fontSize:11}}>
            <span style={{width:8,height:8,borderRadius:'50%',background:tt.c||'#aaa',flexShrink:0,display:'inline-block'}}/>
            <span style={{fontWeight:600}}>{String(sh).padStart(2,'0')}:{String(sm).padStart(2,'0')} → {String(eh).padStart(2,'0')}:{String(em).padStart(2,'0')}</span>
            <span style={{color:'var(--si)'}}>{t.label}</span>
          </div>);
        })}
        {dayTasksForConflict.some(t=>currentSlot&&currentSlot.start<t.end&&currentSlot.end>t.start)&&
          <div style={{fontSize:11,color:'var(--dg)',fontWeight:700,marginTop:2}}>⚠ Ce créneau chevauche une tâche existante</div>}
      </div>}
      {dayTasks.length===0&&<div style={{fontSize:11,color:'var(--ok)',marginTop:4}}>✓ Journée libre pour {assigneeName}</div>}
    </div>)}

    {/* Horaires */}
    {!isP
      ?<div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
          <div><label className="fl">Heure début</label>
            <div style={{display:'flex',gap:4}}><input type="number" min="0" max="23" value={f.sh} onChange={e=>setHour(Number(e.target.value),f.sm)} style={{maxWidth:70}}/><span style={{alignSelf:'center',fontWeight:700}}>h</span><input type="number" min="0" max="59" value={f.sm} onChange={e=>setHour(f.sh,Number(e.target.value))} style={{maxWidth:70}}/></div>
          </div>
          {noApt?(()=>{
            const endTot=f.sh*60+f.sm+f.dm;
            const eh=Math.floor(endTot/60),em=endTot%60;
            const setEnd=(nh,nm)=>{
              const dm=Math.max(15,(nh*60+nm)-(f.sh*60+f.sm));
              set('dm',dm);
            };
            return(<div><label className="fl">Heure fin</label>
              <div style={{display:'flex',gap:4}}><input type="number" min="0" max="23" value={eh} onChange={e=>setEnd(Number(e.target.value),em)} style={{maxWidth:70}}/><span style={{alignSelf:'center',fontWeight:700}}>h</span><input type="number" min="0" max="59" value={em} onChange={e=>setEnd(eh,Number(e.target.value))} style={{maxWidth:70}}/></div>
            </div>);
          })():(
            <div><label className="fl">Durée (min)</label><input type="number" min="15" step="15" value={f.dm} onChange={e=>set('dm',Number(e.target.value))}/></div>
          )}
          <div><label className="fl">Priorité</label><select value={f.prio} onChange={e=>set('prio',e.target.value)}><option value="normal">Normale</option><option value="urgent">Urgente ⚡</option></select></div>
        </div>
      :<div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
          <div><label className="fl">Disponible à partir de</label><input type="time" value={f.af} onChange={e=>set('af',e.target.value)}/></div>
          <div><label className="fl">Terminer avant</label><input type="time" value={f.db} onChange={e=>set('db',e.target.value)}/></div>
          <div><label className="fl">Priorité</label><select value={f.prio} onChange={e=>set('prio',e.target.value)}><option value="normal">Normale</option><option value="urgent">Urgente ⚡</option></select></div>
        </div>
    }

    {/* Check-in : marquer l'heure comme rendez-vous confirmé avec le client (vs défaut 16:00) */}
    {!isP&&f.type==='checkin'&&(
      <div onClick={()=>set('rdv',!f.rdv)} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',background:f.rdv?'#dbeeff':'var(--bg)',border:`1.5px solid ${f.rdv?'#4a9eff':'var(--li)'}`,borderRadius:10,padding:'10px 12px'}}>
        <div style={{width:36,height:20,borderRadius:999,background:f.rdv?'#4a9eff':'var(--li)',position:'relative',flexShrink:0,transition:'background .2s'}}><div style={{position:'absolute',top:2,left:f.rdv?18:2,width:16,height:16,borderRadius:'50%',background:'white',boxShadow:'0 1px 3px rgba(0,0,0,.2)',transition:'left .2s'}}/></div>
        <div><div style={{fontSize:13,fontWeight:600,color:f.rdv?'#1e6fd6':'var(--ink)'}}>📌 Heure de rendez-vous confirmée</div><div style={{fontSize:11,color:'var(--si)'}}>L'heure ci-dessus est le RDV convenu avec le client. Elle sera marquée « RDV » dans le planning.</div></div>
      </div>
    )}

    <div><label className="fl">Notes</label><textarea rows="2" value={f.notes} onChange={e=>set('notes',e.target.value)} placeholder="Instructions particulières…"/></div>

    {/* ── Alerte plein écran (v145) ── Distincte de Notes : consigne à accusé de réception
        obligatoire côté salarié (popup plein écran, fermeture manuelle, réapparaît à chaque
        retour sur l'onglet Tâches tant que la tâche n'est pas terminée). À réserver aux
        consignes vraiment critiques, pas à toute instruction — Notes reste le canal normal. */}
    <div>
      <label className="fl">⚠️ Alerte plein écran (optionnel)</label>
      <textarea rows="2" value={f.alerte} onChange={e=>set('alerte',e.target.value)} placeholder="Ex : Attention, le canapé-lit doit impérativement être fait" style={f.alerte?{border:'1.5px solid #e5a83a',background:'#fffaf0'}:null}/>
      <div style={{fontSize:10,color:'var(--si)',marginTop:2}}>S'affiche en plein écran côté salarié, à fermer manuellement — à réserver aux consignes critiques.</div>
    </div>

    {/* ── Facturation à la semaine (Morceau 1) ──
        Visible seulement si le bien appartient à un client profil 'prestations' + regroupement 'semaine'.
        Tague la tâche : semaine de facturation + jour de sortie réelle. Sert au moteur d'agrégation (Morceau 2). */}
    {!isP&&(f.type==='full'||f.type==='quick'||f.type==='linge'||f.type==='checkin'||f.type==='checkout')&&(()=>{
      const bienSel=biens.find(b=>b.id===f.bid);
      const cli=bienSel?_clients.find(c=>c.id===bienSel.cid):null;
      if(!cli||cli.profilFacturation!=='prestations'||(cli.modeRegroupement||'semaine')!=='semaine') return null;
      const lundiDe=(dateStr)=>{
        if(!dateStr) return '';
        const d=new Date(dateStr+'T12:00:00');
        const jour=(d.getDay()+6)%7; // 0=lundi … 6=dimanche
        d.setDate(d.getDate()-jour);
        return d.toISOString().slice(0,10);
      };
      const labelSemaine=(lundiStr)=>{
        if(!lundiStr) return '';
        const d=new Date(lundiStr+'T12:00:00');
        return 'semaine du lundi '+String(d.getDate()).padStart(2,'0')+'/'+String(d.getMonth()+1).padStart(2,'0');
      };
      const remplirDepuisTache=()=>{
        const sortie=f.jourSortie||f.date;
        setF(x=>({...x,jourSortie:sortie,semaineFacture:lundiDe(sortie)}));
      };
      return(<div style={{background:'#eef4ff',borderRadius:9,padding:'12px 14px',border:'1.5px solid #bfdbfe',display:'flex',flexDirection:'column',gap:10}}>
        <div style={{fontSize:12,color:'#1e40af',lineHeight:1.5}}>
          🧾 <strong>Facturation à la semaine</strong> — {cli.name}. Renseignez le jour de sortie réelle (cette date apparaîtra dans la colonne « Jour » de la facture) ; la semaine de facturation en est déduite automatiquement.
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div>
            <label className="fl">Jour de sortie réelle</label>
            <input type="date" value={f.jourSortie||''} onChange={e=>{
              const v=e.target.value;
              // La semaine de facturation se déduit TOUJOURS du jour de sortie (recalcul systématique).
              setF(x=>({...x,jourSortie:v,semaineFacture:lundiDe(v)}));
            }}/>
          </div>
          <div>
            <label className="fl">Semaine de facturation</label>
            <div style={{padding:'9px 12px',borderRadius:8,background:'#fff',border:'1.5px solid #bfdbfe',fontSize:14,color:f.semaineFacture?'var(--ink)':'var(--si)',minHeight:38,display:'flex',alignItems:'center'}}>
              {f.semaineFacture?labelSemaine(f.semaineFacture):'— renseignez le jour de sortie'}
            </div>
            <div style={{fontSize:10,color:'#1e40af',marginTop:3}}>Calculée automatiquement (lundi de la semaine de sortie).</div>
          </div>
        </div>
        {(!f.jourSortie&&!f.semaineFacture)
          ?<button type="button" onClick={remplirDepuisTache} className="btn bgh sm" style={{alignSelf:'flex-start',fontSize:11}}>↪ Reprendre la date de la tâche ({f.date?f.date.split('-').reverse().join('/'):'—'})</button>
          :<div style={{fontSize:10,color:'var(--si)'}}>Ordre dans la facture : semaine → jour de sortie → ordre du bien (réglé dans la fiche du bien).</div>}
      </div>);
    })()}

    {/* Intervention a l'heure (Chantier C) : rappel tarif + duree manuelle de secours */}
    {!isP&&f.type==='intervention'&&(()=>{
      const b=biens.find(x=>x.id===f.bid);
      const th=Number(b?.tarifInterventionH||0);
      return(<div style={{background:'#e0f2fe',borderRadius:9,padding:'12px',border:'1px solid #7dd3fc',display:'flex',flexDirection:'column',gap:10}}>
        <div style={{fontSize:12,color:'#0c5a82',lineHeight:1.5}}>
          🔧 <strong>Intervention facturée au temps réel.</strong> {th>0
            ? <>Tarif du bien : <strong>{feur(th)}/h</strong> · 1h min, arrondi au quart d'heure. La durée vient du pointage début/fin.</>
            : <span style={{color:'#b45309'}}>⚠️ Aucun tarif intervention/h défini sur ce bien — à renseigner dans la fiche du bien.</span>}
        </div>
        <div>
          <label className="fl">Nature de l'intervention <span style={{color:'var(--si)',fontWeight:400}}>(apparaît sur la facture)</span></label>
          <input type="text" value={f.natureIntervention||''} onChange={e=>set('natureIntervention',e.target.value)} placeholder="ex: Remplacement ampoule + attente plombier" maxLength={120}/>
        </div>
        <div>
          <label className="fl">Durée réelle (min) — si la fin n'est pas pointée</label>
          <input type="number" min="0" step="5" value={f.dureeManuelle||''} onChange={e=>set('dureeManuelle',e.target.value===''?'':Number(e.target.value))} placeholder="laisser vide = utilise le pointage" style={{maxWidth:280}}/>
          <div style={{fontSize:10,color:'#0c5a82',marginTop:3}}>Saisie de secours : prioritaire seulement si aucun pointage de fin n'existe.</div>
        </div>
      </div>);
    })()}

    {/* Surcharge 25% optionnelle pour dimanche / jour férié */}
    <div style={{background:'var(--bg)',borderRadius:9,padding:'10px 12px',border:'1px solid var(--li)'}}>
      <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13}}>
        <input type="checkbox" checked={!!f.surcharge25} onChange={e=>set('surcharge25',e.target.checked)} style={{width:16,height:16,cursor:'pointer'}}/>
        <span><strong>Appliquer la majoration +25%</strong> <span style={{color:'var(--si)',fontSize:11}}>(dimanche / jour férié — à cocher uniquement si applicable)</span></span>
      </label>
    </div>

    {/* À risque : uniquement pour les tâches non affectées */}
    {((!isP&&!f.sid)||(isP&&!f.pid))&&<div style={{background:'#fff4d6',borderRadius:9,padding:'10px 12px',border:'1px solid #e5c977'}}>
      <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13}}>
        <input type="checkbox" checked={!!f.risque} onChange={e=>set('risque',e.target.checked)} style={{width:16,height:16,cursor:'pointer'}}/>
        <span><strong>⚠️ Tâche à risque</strong> <span style={{color:'var(--si)',fontSize:11}}>(le ménage est déjà programmé un autre jour, mais cette date reste à surveiller)</span></span>
      </label>
    </div>}

    {/* Rapport optionnel : ménages (full/quick/linge) + correction, pas les check-in/check-out */}
    {!isP&&(f.type==='full'||f.type==='quick'||f.type==='linge'||f.type==='correction')&&<div style={{background:'var(--bg)',borderRadius:9,padding:'10px 12px',border:'1px solid var(--li)'}}>
      <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13}}>
        <input type="checkbox" checked={!!f.noReport} onChange={e=>set('noReport',e.target.checked)} style={{width:16,height:16,cursor:'pointer'}}/>
        <span><strong>📝 Rapport de ménage optionnel</strong> <span style={{color:'var(--si)',fontSize:11}}>(le salarié pourra pointer la fin sans soumettre de rapport — le bouton 📝 Rapport reste accessible s'il veut le remplir)</span></span>
      </label>
    </div>}

    {/* Options urgentes : uniquement si priorité = urgente, et pour les ménages (full/quick/linge) côté staff */}
    {f.prio==='urgent'&&!isP&&(f.type==='full'||f.type==='quick'||f.type==='linge')&&<div style={{background:'#fde8e8',borderRadius:9,padding:'10px 12px',border:'1px solid #f5b8b8',display:'flex',flexDirection:'column',gap:8}}>
      <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13}}>
        <input type="checkbox" checked={!!f.applyTarifUrgent} onChange={e=>set('applyTarifUrgent',e.target.checked)} style={{width:16,height:16,cursor:'pointer'}}/>
        <span><strong>⚡ Appliquer le tarif urgent</strong> <span style={{color:'var(--si)',fontSize:11}}>(ajoute le supplément urgent défini dans la fiche du bien)</span></span>
      </label>
      {!f.id&&<label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13}}>
        <input type="checkbox" checked={!!f.autoCheckin} onChange={e=>set('autoCheckin',e.target.checked)} style={{width:16,height:16,cursor:'pointer'}}/>
        <span><strong>🔑 Créer aussi un check-in non affecté</strong> <span style={{color:'var(--si)',fontSize:11}}>(même date — à affecter ensuite)</span></span>
      </label>}
    </div>}

    {/* Statut si édition */}
    {f.id&&<div><label className="fl">Statut</label>
      <div style={{display:'flex',gap:8}}>
        {[['todo','À faire'],['progress','En cours'],['done','Fait']].map(([v,l])=>(
          <button key={v} onClick={()=>set('st',v)} className={`btn sm ${f.st===v?'bor':'bgh'}`}>{l}</button>
        ))}
      </div>
    </div>}

    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',paddingTop:6,flexWrap:'wrap',gap:8}}>
      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
        {f.id&&onDelete&&<button onClick={()=>onDelete(f)} className="btn bdg sm"><Ic n="trash" c="w-3.5 h-3.5"/>Supprimer</button>}
        {f.id&&onDuplicate&&<button onClick={()=>onDuplicate(f)} className="btn bgh sm">📋 Dupliquer</button>}
      </div>
      <div style={{display:'flex',gap:8}}>
        <button onClick={onC} className="btn bgh">Annuler</button>
        <button onClick={async()=>{if(saving)return;setSaving(true);try{await onSave(f);}catch(e){console.error('TaskForm save error',e);}finally{setSaving(false);}}} className="btn bor" disabled={saving||!f.date||(!noApt&&!f.bid)}>{saving?'⏳ Enregistrement…':'✓ Enregistrer'}</button>
      </div>
    </div>
  </div>);
}

// ── Helper : CA d'une tâche unique (tarif selon type + tarif urgent + majoration + extras facturés) ──
// Utilisé par WeekPlan (CA hebdo), SecProp (Préparation facturation) et SecAnalyses (Rentabilité par bien)
// pour garantir le même calcul partout. Les extras (billTotal du rapport soumis) ne sont PAS soumis à la
// majoration +25% du dimanche/JF — ce sont des montants fixes par prestation.
// als (optionnel) : pointages, requis pour valoriser une tâche 'intervention' (temps réel × tarif/h).
function caTask(t, bien, rep, als) {
  // Garde-fou : les postes "sans appartement" (garde check-in, boutique) ne sont jamais
  // facturables, même si un bid traînait par erreur sur la tâche.
  if (TT[t.type]?.noBid) return 0;
  if (!bien) return 0;
  // Binôme : la prestation ne se facture qu'une fois, sur le côté "titulaire". La tâche
  // liée (binomeRole==='binome') n'est jamais facturée, même si son propre bid/type portent
  // à confusion — c'est la MÊME intervention, juste répartie sur deux salariés.
  if (t.binomeRole === 'binome') return 0;
  // Correction : jamais facturée au client, quoi qu'il arrive (tarif urgent, majoration,
  // extras du rapport) — garde-fou explicite et non contournable, plutôt que de compter sur
  // l'absence des options correspondantes dans le formulaire (cf. « jamais d'automatisme qui
  // devine », mais ici c'est l'inverse : jamais d'échappatoire qui facture par erreur).
  if (t.type === 'correction') return 0;
  // Intervention a l'heure : valorisee au temps reel pointe (ou duree manuelle), 1h min, quart d'heure.
  // Si als n'est pas fourni (appels legacy), on renvoie 0 — le module facture passe toujours als.
  if (t.type === 'intervention') {
    const m = montantInterventionHT(t, als, bien);
    return m === null ? 0 : m;
  }
  // Tarif de base selon le type de tâche
  let base = 0;
  if (t.type === 'checkin')       base = Number(bien.tarifCheckin || 0);
  else if (t.type === 'checkout') base = Number(bien.tarifCheckout || 0);
  else if (t.type === 'verify')   base = 0;
  else                            base = Number(bien.cp || 0); // full, quick, linge
  // Supplément tarif urgent (s'ajoute au tarif de base, avant majoration)
  if (t.applyTarifUrgent) base += Number(bien.tarifUrgent || 0);
  // Majoration dimanche / jour férié — taux paramétrable par bien (tarifUrgentPct), défaut 25%
  const pctMajo = Number(bien.tarifUrgentPct) > 0 ? Number(bien.tarifUrgentPct) : 25;
  const majore = t.surcharge25 ? base * (1 + pctMajo / 100) : base;
  // Extras facturés via le rapport de fin de ménage soumis (billTotal)
  const r = (rep || []).find(x => x.tid === t.id && x.sub);
  const extras = Number(r?.billTotal || 0);
  return majore + extras;
}

// ══════════════ FACTURATION PRESTATIONS (Morceau 2) ══════════════
// Detection du supplement spa sur le rapport de menage soumis d'une tache.
// Un champ facturable (bien.bf) est "spa" si sa key vaut 'spa' (templates recents)
// OU si son libelle contient "spa" (biens configures avant la preservation de key).
// Retourne { spaFait:bool, montantSpa:number } pour la tache donnee.
function detecterSpaTache(t, bien, rep) {
  const out = { spaFait: false, montantSpa: 0 };
  if (!bien || !Array.isArray(bien.bf) || !bien.bfOn) return out;
  const r = (rep || []).find(x => x.tid === t.id && x.sub);
  if (!r || !r.bill) return out;
  bien.bf.forEach(field => {
    const estSpa = field.key === 'spa' || /spa/i.test(field.lb || '');
    if (!estSpa) return;
    const v = r.bill[field.id];
    // case a cocher (t==='b') cochee, ou quantite numerique > 0
    const coche = field.t === 'n' ? (Number(v) || 0) > 0 : v === true;
    if (coche) {
      out.spaFait = true;
      out.montantSpa += field.t === 'n'
        ? (Number(v) || 0) * (Number(field.tu) || 0)
        : (Number(field.tu) || 0);
    }
  });
  return out;
}

// Detaille une tache pour la facturation "prestations".
// Retourne un objet { section, lignes:[{d, q, pu}], jour, rang } ou null si rien a facturer.
// section : 'menages' | 'checkin' | 'annexe'
// jour : date d'execution affichee (ASCII court genere par l'appelant)
// La majoration dimanche/ferie ressort SEPAREMENT (agregee en section "majoration" par le moteur).
// Retourne aussi { majoration:number } a part (jamais melange aux lignes de la section).
export function detaillerTachePrestation(t, bien, rep, als, feries) {
  if (!t || !bien) return null;
  const r2 = n => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  const rang = Number(bien.rangFacturation) > 0 ? Number(bien.rangFacturation) : 9999;
  // Jour de reference : pour un menage, le jourSortie taggue ; sinon la date de la tache.
  const jour = t.jourSortie || t.date;

  // ── MENAGES (full / quick) ──
  if (t.type === 'full' || t.type === 'quick') {
    const base = Number(bien.cp || 0);
    if (base <= 0 && !(bien.bfOn)) return null;
    const estAnnexe = !!bien.bienAnnexe;
    const { spaFait, montantSpa } = detecterSpaTache(t, bien, rep);
    // Le suffixe spa n'apparait QUE pour les biens reellement equipes d'un champ facturable "spa"
    // (key 'spa' ou libelle contenant "spa") active via bfOn. Biens annexes : jamais de spa.
    const bienASpa = !estAnnexe && !!(bien.bfOn && Array.isArray(bien.bf) && bien.bf.some(f => f.key === 'spa' || /spa/i.test(f.lb || '')));
    const suffixe = bienASpa ? (spaFait ? ' avec spa' : ' sans spa') : '';
    const lignes = [];
    // Ligne menage : prix de base + supplement spa integre dans le PU (1 ligne unique, comme la facture cible).
    // Ménage URGENT (case « Appliquer le tarif urgent », flag applyTarifUrgent) : le supplément
    // `bien.tarifUrgent` s'ajoute au PU de CETTE ligne (ménage 40 + urgent 10 = 50), jamais une ligne
    // séparée ni un cumul : le client voit quel ménage est concerné. `urgent:true` → pastille PDF.
    const supUrgent = t.applyTarifUrgent ? r2(Number(bien.tarifUrgent || 0)) : 0;
    const estUrgent = supUrgent > 0;
    const puMenage = r2(base + supUrgent + (bienASpa && spaFait ? montantSpa : 0));
    if (puMenage > 0) lignes.push({ d: bien.name + suffixe, q: 1, pu: puMenage, _uniteAnnexe: estAnnexe, ...(estUrgent ? { urgent: true } : {}) });
    // Autres extras du rapport (hors spa, deja integre ci-dessus) → lignes annexes distinctes.
    const r = (rep || []).find(x => x.tid === t.id && x.sub);
    if (r && r.bill && Array.isArray(bien.bf) && bien.bfOn) {
      bien.bf.forEach(field => {
        const estSpa = field.key === 'spa' || /spa/i.test(field.lb || '');
        if (estSpa) return; // spa deja compte
        const v = r.bill[field.id];
        const coche = field.t === 'n' ? (Number(v) || 0) > 0 : v === true;
        if (!coche) return;
        const montant = field.t === 'n'
          ? r2((Number(v) || 0) * (Number(field.tu) || 0))
          : r2(Number(field.tu) || 0);
        const lblFact = (field.lbFact && field.lbFact.trim()) || field.lb || 'Extra';
        if (montant > 0) lignes.push({ d: lblFact + ' - ' + bien.name, q: 1, pu: montant, _annexe: true });
      });
    }
    // Majoration dimanche/ferie (separee, agregee par le moteur, distinguee dimanche vs ferie).
    const pctMajo = Number(bien.tarifUrgentPct) > 0 ? Number(bien.tarifUrgentPct) : 25;
    // Base de la majoration = ménage + supplément urgent (décision Claire 01/10/2026, alignée sur caTask) ;
    // le spa reste hors base (règle historique).
    const majoration = t.surcharge25 ? r2((base + supUrgent) * pctMajo / 100) : 0;
    // Type : ferie prioritaire sur dimanche (un ferie qui tombe un dimanche compte comme ferie).
    let majoType = null;
    if (majoration > 0) {
      const estFerie = (feries || []).includes(jour);
      const estDimanche = jour ? new Date(jour + 'T12:00:00').getDay() === 0 : false;
      majoType = estFerie ? 'ferie' : (estDimanche ? 'dimanche' : 'dimanche');
    }
    if (!lignes.length && majoration <= 0) return null;
    return { section: estAnnexe ? 'annexe' : 'menages', lignes, jour, rang, majoration, majoType };
  }

  // ── CHECK-IN ──
  if (t.type === 'checkin') {
    const pu = Number(bien.tarifCheckin || 0);
    if (pu <= 0) return null;
    const base = pu;
    // Majoration check-in (dimanche/ferie) — agregee a part, distinguee dimanche vs ferie.
    const pctMajo = Number(bien.tarifUrgentPct) > 0 ? Number(bien.tarifUrgentPct) : 25;
    const majoration = t.surcharge25 ? r2(base * pctMajo / 100) : 0;
    let majoType = null;
    if (majoration > 0) {
      const estFerie = (feries || []).includes(jour);
      const estDimanche = jour ? new Date(jour + 'T12:00:00').getDay() === 0 : false;
      majoType = estFerie ? 'ferie' : (estDimanche ? 'dimanche' : 'dimanche');
    }
    return { section: 'checkin', lignes: [{ d: 'Check-in ' + bien.name, q: 1, pu: r2(base) }], jour, rang, majoration, majoType };
  }

  // ── INTERVENTION (a l'heure) → prestations annexes ──
  if (t.type === 'intervention') {
    const m = montantInterventionHT(t, als, bien);
    if (m === null || m <= 0) return null;
    const lbl = (t.natureIntervention && t.natureIntervention.trim())
      ? t.natureIntervention.trim()
      : 'Intervention ' + bien.name;
    return { section: 'annexe', lignes: [{ d: lbl, q: 1, pu: r2(m) }], jour, rang, majoration: 0 };
  }

  // checkout / linge / verify : non facturables en profil prestations pour l'instant.
  return null;
}

// ══════════════ PLANNING HEBDO ══════════════
function WeekPlan({st,setSt,pj,setPj,staff,prov,biens,leave:leaveProp,arretsMaladie,unavail,als,scopeS,scopeP,rep,clients,sejours,statutAppts,setStatutAppts,tc,cpSoldes,rhSettings}){
  // (v239) Les arrêts maladie déclarés dans RH comptent comme des absences au planning. Fournis par AdminUI (tous) et par StaffUI/GerantUI (les siens uniquement : on ne montre jamais l'arrêt d'un autre).
  const leave=useMemo(()=>absencesPourPlanning(leaveProp,arretsMaladie),[leaveProp,arretsMaladie]);
  const[ws,setWs]=useState(()=>gMon(new Date()));
  const days=useMemo(()=>Array.from({length:7},(_,i)=>aD(ws,i)),[ws]);
  const todayS=iD(new Date());
  const isAdmin=!scopeS&&!scopeP&&setSt&&setPj;

  // ── Filtre planning : Bien / Propriétaire ──
  // Lecture seule : ne touche à aucune écriture, ni aux calculs RH/paie (déficit,
  // minutesJour/Semaine) qui doivent rester sur la charge réelle du salarié.
  const[filtCid,setFiltCid]=useState('');
  const[filtBid,setFiltBid]=useState('');
  const[hideEmpty,setHideEmpty]=useState(false);
  const filtreActif=!!(filtCid||filtBid);
  const matchFiltre=bid=>{
    if(!filtreActif)return true;
    if(filtBid)return bid===filtBid;
    const b=biens.find(x=>x.id===bid);
    return !!b&&b.cid===filtCid;
  };
  // Biens proposés dans le sélecteur "Bien" : limités au propriétaire choisi s'il y en a un
  const biensFiltre=filtCid?biens.filter(b=>b.cid===filtCid):biens;

  // ── Modale saisie directe arrivées/départs ──
  const[adModal,setAdModal]=useState(null); // {type:'arr'|'dep', dateStr, entries:[{bid,heure}]}
  const openAdModal=(type,dateStr)=>{
    if(!isAdmin)return;
    // Récupérer les entrées existantes dans statutAppts7
    const key=`ad_${type}_${dateStr}`;
    const existing=(statutAppts||{})[key]||[];
    // Cases auto : "+ ménage" cochée par défaut sur les départs, "+ check-in" cochée par
    // défaut sur les arrivées (garde anti-doublon à l'enregistrement).
    setAdModal({type,dateStr,entries:existing.length>0?existing.map(e=>({...e,creerMenage:type==='dep'?(e.creerMenage!==false):false,creerCheckin:type==='arr'?(e.creerCheckin!==false):false})):[{bid:'',heure:type==='arr'?'16:00':'10:00',creerMenage:type==='dep',creerCheckin:type==='arr'}]});
  };
  const saveAdModal=()=>{
    if(!adModal)return;
    const key=`ad_${adModal.type}_${adModal.dateStr}`;
    const valid=adModal.entries.filter(e=>e.bid);
    const next={...(statutAppts||{}),[key]:valid};
    setStatutAppts(next);
    // ── Création auto de la tâche ménage pour les départs cochés ──
    // Garde anti-doublon : on ne crée rien si une tâche ménage (full/quick/linge) existe déjà ce jour-là pour ce bien.
    // Pas de synchro rétroactive : si le départ est modifié/supprimé ensuite, la tâche créée n'est pas touchée.
    if(adModal.type==='dep'){
      const dateStr=adModal.dateStr;
      // Même logique que "Reprendre la date de la tâche" dans TaskForm (facturation à la semaine) :
      // jourSortie = date du départ, semaineFacture = lundi de cette semaine-là.
      const lundiDe=(ds)=>{
        const d=new Date(ds+'T12:00:00');
        const jour=(d.getDay()+6)%7; // 0=lundi … 6=dimanche
        d.setDate(d.getDate()-jour);
        return d.toISOString().slice(0,10);
      };
      // Détection arrivée le même jour (relais, toutes sources confondues via adForDay) → ménage en urgent.
      // Suppose que les arrivées du jour sont saisies avant les départs (ordre recommandé à Claire).
      const arriveeBids=new Set(adForDay('arr',dateStr).map(a=>a.bid));
      const menageExistant=new Set(st.filter(t=>t.date===dateStr&&['full','quick','linge'].includes(t.type)&&t.st!=='cancelled').map(t=>t.bid));
      const nouvelles=valid.filter(e=>e.creerMenage&&!menageExistant.has(e.bid)).map(e=>{
        const[h,m]=(e.heure||'10:00').split(':').map(Number);
        const bien=biens.find(b=>b.id===e.bid);
        const relais=arriveeBids.has(e.bid);
        return{
          id:'t'+Date.now()+Math.random().toString(36).slice(2,7),
          sid:null,bid:e.bid,type:'full',date:dateStr,
          sh:h||0,sm:m||0,
          dm:Number(bien?.dmFull)||90,
          notes:'Ménage créé automatiquement depuis départ saisi'+(relais?' (relais : arrivée le même jour)':''),
          st:'todo',prio:relais?'urgent':'normal',
          rs:null,re:null,surcharge25:false,risque:false,applyTarifUrgent:false,noReport:false,
          jourSortie:dateStr,semaineFacture:lundiDe(dateStr)
        };
      });
      if(nouvelles.length>0)setSt([...st,...nouvelles]);
    }
    // ── Création auto de la tâche check-in pour les arrivées cochées ──
    // Uniquement pour les biens dont la conciergerie gère le check-in (bien.prestations
    // inclut 'checkin'). Garde anti-doublon : rien si une tâche check-in existe déjà ce
    // jour-là pour ce bien — couvre aussi les check-in créés par l'iCal Eviivo, donc pas
    // de doublon. Pas de synchro rétroactive (modifier/supprimer l'arrivée ne touche pas
    // la tâche créée), même politique que "+ ménage" côté départs.
    // ⚠️ DETTE ASSUMÉE : 'checkin ∈ prestations' est un flag de FACTURATION, pas un flag
    // opérationnel "on assure l'accueil". Praline (accueil non facturé) est déjà couvert
    // par l'iCal ; à remplacer par un tableau de configuration dédié au lancement SaaS.
    if(adModal.type==='arr'){
      const dateStr=adModal.dateStr;
      const checkinExistant=new Set(st.filter(t=>t.date===dateStr&&t.type==='checkin'&&t.st!=='cancelled').map(t=>t.bid));
      const nouvelles=valid.filter(e=>{
        if(!e.creerCheckin||checkinExistant.has(e.bid))return false;
        const bien=biens.find(b=>b.id===e.bid);
        return (bien?.prestations||[]).includes('checkin');
      }).map(e=>{
        const[h,m]=(e.heure||'16:00').split(':').map(Number);
        return{
          id:'t'+Date.now()+Math.random().toString(36).slice(2,7),
          sid:null,bid:e.bid,type:'checkin',date:dateStr,
          sh:Number.isFinite(h)?h:16,sm:Number.isFinite(m)?m:0,
          dm:15,
          notes:'Check-in créé automatiquement depuis arrivée saisie',
          st:'todo',prio:'normal',
          rs:null,re:null,surcharge25:false,risque:false,applyTarifUrgent:false
        };
      });
      if(nouvelles.length>0)setSt([...st,...nouvelles]);
    }
    setAdModal(null);
  };

  // ── Calcul arrivées/départs par jour ──
  // Source 1 : tâches checkin/checkout dans st7
  // Source 2 : séjours iCal (sejours7) — dateArrivee pour arr, dateDepart pour dep
  // Source 3 : saisies directes dans statutAppts7 (clé ad_arr_YYYY-MM-DD / ad_dep_YYYY-MM-DD)
  const adForDay=(type,dateStr)=>{
    const result=[];
    // Source 1 : tâches
    const taskType=type==='arr'?'checkin':'checkout';
    st.filter(t=>t.date===dateStr&&t.type===taskType&&t.bid).forEach(t=>{
      const b=biens.find(x=>x.id===t.bid);
      if(b)result.push({bid:t.bid,name:b.name,heure:`${String(t.sh).padStart(2,'0')}:${String(t.sm).padStart(2,'0')}`,src:'task'});
    });
    // Source 2 : iCal Eviivo
    if(Array.isArray(sejours)){
      sejours.filter(s=>s.statut!=='ignore'&&s.bid&&(type==='arr'?s.dateArrivee===dateStr:s.dateDepart===dateStr)).forEach(s=>{
        if(result.some(r=>r.bid===s.bid))return; // déjà couvert par une tâche
        const b=biens.find(x=>x.id===s.bid);
        if(b)result.push({bid:s.bid,name:b.name,heure:type==='arr'?'16:00':'10:00',src:'ical'});
      });
    }
    // Source 3 : saisies directes
    const key=`ad_${type}_${dateStr}`;
    ((statutAppts||{})[key]||[]).forEach(e=>{
      if(!e.bid||result.some(r=>r.bid===e.bid))return;
      const b=biens.find(x=>x.id===e.bid);
      if(b)result.push({bid:e.bid,name:b.name,heure:e.heure||'—',src:'manual'});
    });
    return result;
  };
  // Niveau canicule (pour repérage visuel heures chaudes)
  const[caniNiveau,setCaniNiveau]=useState(null);
  useEffect(()=>{
    const fetchCani=()=>supaGet('canicule7').then(d=>{setCaniNiveau(d?.niveau||null);}).catch(()=>{});
    fetchCani();
    const id=setInterval(fetchCani,5*60*1000);
    return()=>clearInterval(id);
  },[]);
  const vS=scopeS?staff.filter(s=>s.id===scopeS):staff.filter(s=>{
    // Cacher dans le planning si le salarié est archivé ET que la semaine affichée
    // commence APRÈS sa date de fin de contrat. Sa dernière semaine reste affichée.
    // Étape 1B : la fin de contrat se lit sur le DERNIER contrat, plus sur un
    // champ plat. Un salarié réembauché a un contrat en cours (endDate null)
    // → il réapparaît naturellement dans le planning.
    const dc=dernierContrat(s);
    if(s.archived&&dc?.endDate&&dc.endDate<iD(ws))return false;
    return true;
  });
  const vP=scopeP?prov.filter(p=>p.id===scopeP):prov.filter(p=>p.active);
  // Lignes affichées : filtre bien/propriétaire + masquage optionnel des lignes sans
  // tâche correspondante cette semaine. vS/vP restent la liste complète (comptages,
  // logique existante) ; vSAff/vPAff ne servent qu'au rendu des lignes.
  const wkStart=iD(ws),wkEnd=iD(aD(ws,6));
  const vSAff=(filtreActif&&hideEmpty)?vS.filter(s=>st.some(t=>t.sid===s.id&&t.date>=wkStart&&t.date<=wkEnd&&matchFiltre(t.bid))):vS;
  const vPAff=(filtreActif&&hideEmpty)?vP.filter(p=>pj.some(j=>j.pid===p.id&&j.date>=wkStart&&j.date<=wkEnd&&matchFiltre(j.bid))):vP;
  const stFor=(sid,d)=>st.filter(t=>t.sid===sid&&t.date===iD(d)&&matchFiltre(t.bid)).sort((a,b)=>a.sh-b.sh);
  const pvFor=(pid,d)=>pj.filter(j=>j.pid===pid&&j.date===iD(d)&&matchFiltre(j.bid));
  // Helpers heures planifiées par salarié
  const minutesJour=(sid,d)=>st.filter(t=>t.sid===sid&&t.date===iD(d)).reduce((s,t)=>s+(t.dm||0),0);
  // Créneau extensible (demande Claire, 16/08/2026) : mêmes tâches BRUTES que
  // minutesJour (non filtrées par matchFiltre) — un salarié qui regarde son
  // propre planning n'a de toute façon aucun filtre Bien/Propriétaire actif.
  const tpj=s=>(d)=>travailPossibleJusqua(s,iD(d),st.filter(t=>t.sid===s.id&&t.date===iD(d)));
  // Note de bas de tableau (côté salarié uniquement) : au moins un jour de la semaine affiche le
  // bandeau "Peut travailler jusqu'à" pour ce salarié — évite de répéter le texte dans chaque case.
  const anyTpThisWeek=scopeS&&vSAff[0]?days.some(d=>{
    const dI=iD(d);
    const absJour=leave.find(l=>l.sid===vSAff[0].id&&l.st==='approved'&&dI>=l.sd&&dI<=l.ed);
    if(absJour)return false;
    if(dI<todayS)return false;
    return !!tpj(vSAff[0])(d);
  }):false;
  const minutesSemaine=sid=>{
    const start=iD(ws),end=iD(aD(ws,6));
    return st.filter(t=>t.sid===sid&&t.date>=start&&t.date<=end).reduce((s,t)=>s+(t.dm||0),0);
  };
  // Formate des minutes en "Xh" ou "XhY" ou "—" si 0
  const fhm=min=>{if(!min)return null;const h=Math.floor(min/60),m=min%60;return m===0?`${h}h`:`${h}h${String(m).padStart(2,'0')}`;};
  // Tâches non affectées SANS risque (vraie alerte d'affectation) : tâches salariés sans sid + missions prestataires sans pid
  // Chaque élément est taggé avec _src ('st' ou 'pj') pour savoir comment l'éditer
  const unFor=d=>{
    const dI=iD(d);
    const tachesNA=st.filter(t=>!t.sid&&!t.risque&&t.st!=='cancelled'&&t.date===dI&&matchFiltre(t.bid)).map(t=>({...t,_src:'st'}));
    const missionsNA=pj.filter(j=>!j.pid&&!j.risque&&j.date===dI&&matchFiltre(j.bid)).map(j=>({...j,_src:'pj'}));
    return [...tachesNA,...missionsNA];
  };
  // Tâches à risque : non affectées mais marquées comme couvertes ailleurs (à surveiller)
  const riskFor=d=>{
    const dI=iD(d);
    const tachesR=st.filter(t=>!t.sid&&t.risque&&t.st!=='cancelled'&&t.date===dI&&matchFiltre(t.bid)).map(t=>({...t,_src:'st'}));
    const missionsR=pj.filter(j=>!j.pid&&j.risque&&j.date===dI&&matchFiltre(j.bid)).map(j=>({...j,_src:'pj'}));
    return [...tachesR,...missionsR];
  };
  const totUn=st.filter(t=>!t.sid&&!t.risque&&t.st!=='cancelled'&&t.date>=iD(ws)&&t.date<=iD(aD(ws,6))&&matchFiltre(t.bid)).length
            +pj.filter(j=>!j.pid&&!j.risque&&j.date>=iD(ws)&&j.date<=iD(aD(ws,6))&&matchFiltre(j.bid)).length;
  const totRisk=st.filter(t=>!t.sid&&t.risque&&t.st!=='cancelled'&&t.date>=iD(ws)&&t.date<=iD(aD(ws,6))&&matchFiltre(t.bid)).length
              +pj.filter(j=>!j.pid&&j.risque&&j.date>=iD(ws)&&j.date<=iD(aD(ws,6))&&matchFiltre(j.bid)).length;
  const totUrg=st.filter(t=>t.prio==='urgent'&&t.st!=='done'&&t.date>=iD(ws)&&t.date<=iD(aD(ws,6))&&matchFiltre(t.bid)).length
              +pj.filter(j=>j.prio==='urgent'&&j.st!=='done'&&j.date>=iD(ws)&&j.date<=iD(aD(ws,6))&&matchFiltre(j.bid)).length;
  // 🔍 Diagnostic : tâches orphelines (affectées à des salariés/prestataires qui n'existent plus)
  // Sur l'ENSEMBLE de la base, pas juste la semaine affichée, pour faciliter le nettoyage
  const validStaffIds=useMemo(()=>new Set(staff.map(s=>s.id)),[staff]);
  const validProvIds=useMemo(()=>new Set(prov.map(p=>p.id)),[prov]);
  const orphansSt=useMemo(()=>st.filter(t=>t.sid&&!validStaffIds.has(t.sid)).map(t=>({...t,_src:'st'})),[st,validStaffIds]);
  const orphansPj=useMemo(()=>pj.filter(j=>j.pid&&!validProvIds.has(j.pid)).map(j=>({...j,_src:'pj'})),[pj,validProvIds]);
  const totOrph=orphansSt.length+orphansPj.length;
  const[showOrph,setShowOrph]=useState(false);

  // Modal création/édition
  const[modal,setModal]=useState(null); // {prefill}
  const openNew=(prefill={})=>isAdmin&&setModal({prefill});
  const saveTask=async f=>{
    // ─── READ-BEFORE-WRITE (fix bugs multi-session) ───
    // On lit la version FRAÎCHE de st7 et pj7 depuis Supabase avant d'écrire,
    // pour ne pas écraser les modifs faites par l'autre admin entre temps.
    // En cas d'échec réseau, on retombe sur le state local (best effort).
    let freshSt=st, freshPj=pj;
    try{
      const[remoteSt,remotePj]=await Promise.all([supaGet('st7'),supaGet('pj7')]);
      if(Array.isArray(remoteSt))freshSt=remoteSt;
      if(Array.isArray(remotePj))freshPj=remotePj;
    }catch(e){
      console.warn('[saveTask] Lecture fraîche échouée, fallback sur state local',e.message);
    }
    // Si on change de type (staff↔provider), supprimer l'ancienne entrée
    let nextSt=freshSt, nextPj=freshPj;
    if(f.id){
      const wasStaff=freshSt.some(t=>t.id===f.id);
      const wasProvider=freshPj.some(j=>j.id===f.id);
      if(wasStaff&&f.assigneeType==='provider') nextSt=nextSt.filter(t=>t.id!==f.id);
      if(wasProvider&&f.assigneeType==='staff') nextPj=nextPj.filter(j=>j.id!==f.id);
    }
    if(f.assigneeType==='provider'){
      // IMPORTANT : on cherche l'ID dans nextPj (version fraîche), pas dans pj local stale
      const job={id:f.id&&nextPj.some(j=>j.id===f.id)?f.id:'j'+Date.now(),bid:f.bid,pid:f.pid||null,date:f.date,af:f.af||'09:00',db:f.db||'17:00',st:f.st||'todo',prio:f.prio||'normal',notes:f.notes,dm:f.dm||90,surcharge25:!!f.surcharge25,risque:!f.pid&&!!f.risque,applyTarifUrgent:f.prio==='urgent'&&!!f.applyTarifUrgent};
      // ─── notif : tache_affectee (provider) ───
      // On émet si : (1) nouvelle tâche avec un prestataire, OU (2) tâche existante dont le prestataire change
      const previousJob=freshPj.find(j=>j.id===job.id);
      const wasUnassigned=!previousJob||!previousJob.pid;
      const isNowAssigned=!!job.pid;
      const assigneeChanged=previousJob&&previousJob.pid&&previousJob.pid!==job.pid;
      if(isNowAssigned && (wasUnassigned||assigneeChanged)){
        const bien=biens.find(b=>b.id===job.bid);
        notifEventBus.emit('tache_affectee',{
          toUserType:'provider', toUserId:job.pid,
          taskId:job.id, taskType:'prestataire', date:job.date,
          summary:`Nouvelle mission le ${job.date} à ${bien?.name||'?'} (${job.af}-${job.db})`
        });
      } else if(isNowAssigned && previousJob && !assigneeChanged){
        // ─── notif : tache_modifiee (provider) ───
        // Même prestataire, mais un détail a changé : date, horaires ou bien.
        const changed = previousJob.date!==job.date || previousJob.af!==job.af
          || previousJob.db!==job.db || previousJob.bid!==job.bid;
        if(changed){
          const bien=biens.find(b=>b.id===job.bid);
          notifEventBus.emit('tache_modifiee',{
            toUserType:'provider', toUserId:job.pid,
            taskId:job.id, taskType:'prestataire', date:job.date,
            summary:`Mission modifiée le ${job.date} à ${bien?.name||'?'} (${job.af}-${job.db})`
          });
        }
      }
      nextPj=nextPj.some(j=>j.id===job.id)?nextPj.map(j=>j.id===job.id?job:j):[...nextPj,job];
      setPj(nextPj);
      if(nextSt!==freshSt)setSt(nextSt); // si on a fait un type-switch
    } else {
      // IMPORTANT : on cherche l'ID dans nextSt (version fraîche), pas dans st local stale
      const editingId=f.id&&nextSt.some(t=>t.id===f.id)?f.id:null;
      const previousTask=editingId?freshSt.find(t=>t.id===editingId):null;

      // ─── Binôme : résolution état (voir HANDOFF binôme) ───
      // wasTitulaireLinked : la tâche éditée était déjà le côté "titulaire" d'un binôme.
      // Un déplacement de DATE sur le titulaire casse le lien et désaffecte le binôme (non affectée) —
      // un simple changement d'heure/durée ne casse rien (chaque côté reste modifiable indépendamment).
      const wasTitulaireLinked=previousTask?.binomeRole==='titulaire'&&!!previousTask?.groupeBinome;
      const dateChanged=previousTask&&previousTask.date!==f.date;
      const dissocierParDeplacement=wasTitulaireLinked&&dateChanged;
      const dissocierExplicite=!!f._dissocierBinome; // bouton "Dissocier" côté binome, ou décochage côté titulaire
      const decocheBinome=wasTitulaireLinked&&!f.binome;
      const doitDissocier=dissocierParDeplacement||dissocierExplicite||decocheBinome;
      // Seul un déplacement de date désaffecte le binôme ; dissociation manuelle = on délie sans toucher
      // à son affectation (philosophie "pas de suppression destructrice").
      const alsoUnassignSibling=dissocierParDeplacement;

      const creeBinome=!doitDissocier&&f.binomeRole!=='binome'&&f.binome&&!!f.binomeSid;
      const activationBinome=creeBinome&&!previousTask?.groupeBinome; // 1ʳᵉ mise en binôme → on coupe la durée en 2
      const dmFinal=activationBinome?Math.max(1,Math.round((f.dm||60)/2)):(f.dm||60);
      const noReportFinal=creeBinome?(f.binomeRapport==='binome'):!!f.noReport;

      let groupeBinomeFinal=null, binomeRoleFinal=null;
      if(creeBinome){
        groupeBinomeFinal=previousTask?.groupeBinome||('bin'+Date.now());
        binomeRoleFinal='titulaire';
      } else if(!doitDissocier&&f.binomeRole==='binome'){
        groupeBinomeFinal=f.groupeBinome; binomeRoleFinal='binome';
      }

      const task={id:editingId||'t'+Date.now(),sid:f.sid||null,bid:f.bid,type:f.type||'full',date:f.date,sh:f.sh??9,sm:f.sm??0,dm:dmFinal,notes:f.notes,alerte:f.alerte||'',st:f.st||'todo',prio:f.prio||'normal',rs:null,re:null,surcharge25:!!f.surcharge25,risque:!f.sid&&!!f.risque,applyTarifUrgent:f.prio==='urgent'&&!!f.applyTarifUrgent,noReport:noReportFinal,dureeManuelle:Number(f.dureeManuelle)>0?Number(f.dureeManuelle):null,natureIntervention:f.natureIntervention||'',semaineFacture:f.semaineFacture||'',jourSortie:f.jourSortie||'',rdv:f.type==='checkin'&&!!f.rdv,groupeBinome:groupeBinomeFinal,binomeRole:binomeRoleFinal};

      // ─── Dissociation : délie l'autre moitié (et la désaffecte si déplacement de date) ───
      if(doitDissocier){
        const groupeADissocier=previousTask?.groupeBinome||f.groupeBinome;
        if(groupeADissocier){
          nextSt=nextSt.map(t=>{
            if(t.groupeBinome!==groupeADissocier||t.id===task.id)return t;
            return alsoUnassignSibling?{...t,sid:null,groupeBinome:null,binomeRole:null}:{...t,groupeBinome:null,binomeRole:null};
          });
        }
      }

      // ─── Binôme actif : construit/synchronise la tâche liée ───
      let siblingTask=null;
      if(creeBinome){
        const existingSibling=groupeBinomeFinal?nextSt.find(t=>t.groupeBinome===groupeBinomeFinal&&t.id!==task.id):null;
        const partnerChanged=existingSibling&&existingSibling.sid!==f.binomeSid;
        if(partnerChanged){
          // L'ancien binôme n'est pas supprimé : délié et repart en non-affectée.
          nextSt=nextSt.map(t=>t.id===existingSibling.id?{...t,sid:null,groupeBinome:null,binomeRole:null}:t);
        }
        const reuseSibling=(existingSibling&&!partnerChanged)?existingSibling:null;
        // alerte propagée telle quelle sur la tâche liée : l'un ou l'autre salarié peut être
        // sur place, la consigne doit être visible des deux côtés (même logique que notes).
        siblingTask={id:reuseSibling?.id||('t'+Date.now()+'_bin'),sid:f.binomeSid,bid:task.bid,type:task.type,date:task.date,sh:reuseSibling?reuseSibling.sh:task.sh,sm:reuseSibling?reuseSibling.sm:task.sm,dm:reuseSibling?reuseSibling.dm:dmFinal,notes:reuseSibling?.notes||task.notes,alerte:task.alerte,st:reuseSibling?.st||'todo',prio:task.prio,rs:reuseSibling?.rs??null,re:reuseSibling?.re??null,surcharge25:task.surcharge25,risque:false,applyTarifUrgent:task.applyTarifUrgent,noReport:f.binomeRapport==='binome'?false:true,dureeManuelle:null,natureIntervention:task.natureIntervention,semaineFacture:task.semaineFacture,jourSortie:task.jourSortie,rdv:false,groupeBinome:groupeBinomeFinal,binomeRole:'binome'};
      }

      // ─── notif : tache_affectee (staff) ───
      const wasUnassigned=!previousTask||!previousTask.sid;
      const isNowAssigned=!!task.sid;
      const assigneeChanged=previousTask&&previousTask.sid&&previousTask.sid!==task.sid;
      if(isNowAssigned && (wasUnassigned||assigneeChanged)){
        const bien=biens.find(b=>b.id===task.bid);
        const heure=`${String(task.sh).padStart(2,'0')}:${String(task.sm).padStart(2,'0')}`;
        notifEventBus.emit('tache_affectee',{
          toUserType:'staff', toUserId:task.sid,
          taskId:task.id, taskType:task.type, date:task.date,
          summary:`Nouvelle tâche ${task.type||''} le ${task.date} à ${heure} · ${bien?.name||'?'}`
        });
      } else if(isNowAssigned && previousTask && !assigneeChanged){
        // ─── notif : tache_modifiee (staff) ───
        // Même salarié, mais un détail a changé : date, heure, durée ou bien.
        const changed = previousTask.date!==task.date || previousTask.sh!==task.sh
          || previousTask.sm!==task.sm || previousTask.dm!==task.dm
          || previousTask.bid!==task.bid || previousTask.type!==task.type;
        if(changed){
          const bien=biens.find(b=>b.id===task.bid);
          const heure=`${String(task.sh).padStart(2,'0')}:${String(task.sm).padStart(2,'0')}`;
          notifEventBus.emit('tache_modifiee',{
            toUserType:'staff', toUserId:task.sid,
            taskId:task.id, taskType:task.type, date:task.date,
            summary:`Tâche modifiée le ${task.date} à ${heure} · ${bien?.name||'?'}`
          });
        }
      }
      nextSt=nextSt.some(t=>t.id===task.id)?nextSt.map(t=>t.id===task.id?task:t):[...nextSt,task];
      if(siblingTask){
        const wasSiblingUnassigned=!nextSt.some(t=>t.id===siblingTask.id&&t.sid);
        nextSt=nextSt.some(t=>t.id===siblingTask.id)?nextSt.map(t=>t.id===siblingTask.id?siblingTask:t):[...nextSt,siblingTask];
        // ─── notif : tache_affectee (binôme) ───
        if(wasSiblingUnassigned){
          const bienBin=biens.find(b=>b.id===siblingTask.bid);
          const heureBin=`${String(siblingTask.sh).padStart(2,'0')}:${String(siblingTask.sm).padStart(2,'0')}`;
          notifEventBus.emit('tache_affectee',{
            toUserType:'staff', toUserId:siblingTask.sid,
            taskId:siblingTask.id, taskType:siblingTask.type, date:siblingTask.date,
            summary:`Nouvelle tâche en binôme ${siblingTask.type||''} le ${siblingTask.date} à ${heureBin} · ${bienBin?.name||'?'}`
          });
        }
      }
      // Création auto d'un check-in non affecté si demandé (uniquement à la création de la tâche urgente)
      if(!f.id&&f.autoCheckin&&f.prio==='urgent'){
        const checkinTask={
          id:'t'+Date.now()+'_ci',
          sid:null, // toujours non affecté
          bid:f.bid,
          type:'checkin',
          date:f.date,
          sh:16,sm:0,
          dm:15,
          notes:'Check-in créé automatiquement depuis tâche urgente',
          st:'todo',
          prio:'urgent',
          rs:null,re:null,
          surcharge25:false,
          risque:false,
          applyTarifUrgent:false,
        };
        nextSt=[...nextSt,checkinTask];
      }
      setSt(nextSt);
      if(nextPj!==freshPj)setPj(nextPj); // si on a fait un type-switch
    }
    setModal(null);
  };
  const delTask=async f=>{
    const b=biens.find(x=>x.id===f.bid);
    const dateStr=f.date?new Date(f.date+'T12:00').toLocaleDateString('fr-FR'):'';
    const heure=(f.sh!==undefined&&f.sm!==undefined)?` à ${String(f.sh).padStart(2,'0')}:${String(f.sm).padStart(2,'0')}`:'';
    const msg=`Supprimer définitivement cette tâche ?\n\n${b?.name||'(bien inconnu)'} · ${dateStr}${heure}\n\nCette action est irréversible.`;
    if(!window.confirm(msg))return;
    if(f.assigneeType==='provider'){
      const current=await supaGet('pj7')||[];
      const target=current.find(j=>j.id===f.id);
      const updated=current.filter(j=>j.id!==f.id);
      await supaSet('pj7',updated);
      setPj(updated);
      // ─── notif : tache_annulee (provider) ───
      if(target&&target.pid){
        notifEventBus.emit('tache_annulee',{
          toUserType:'provider', toUserId:target.pid,
          taskId:target.id, taskType:'prestataire', date:target.date,
          summary:`Mission annulée le ${target.date} à ${b?.name||'?'} (${target.af||''}-${target.db||''})`
        });
      }
    } else {
      const current=await supaGet('st7')||[];
      const target=current.find(t=>t.id===f.id);
      let updated=current.filter(t=>t.id!==f.id);
      // ─── Binôme : la tâche liée n'est jamais supprimée avec le titulaire (pas de suppression
      // destructrice) — elle est déliée. Si c'est le TITULAIRE qui est supprimé, elle repart
      // en plus en non-affectée (même règle que pour un déplacement de date, cf. saveTask).
      if(target?.groupeBinome){
        const desaffecter=target.binomeRole==='titulaire';
        updated=updated.map(t=>{
          if(t.groupeBinome!==target.groupeBinome)return t;
          return desaffecter?{...t,sid:null,groupeBinome:null,binomeRole:null}:{...t,groupeBinome:null,binomeRole:null};
        });
      }
      await supaSet('st7',updated);
      setSt(updated);
      // ─── notif : tache_annulee (staff) ───
      if(target&&target.sid){
        const heure=`${String(target.sh).padStart(2,'0')}:${String(target.sm).padStart(2,'0')}`;
        notifEventBus.emit('tache_annulee',{
          toUserType:'staff', toUserId:target.sid,
          taskId:target.id, taskType:target.type, date:target.date,
          summary:`Tâche annulée le ${target.date} à ${heure} · ${b?.name||'?'}`
        });
      }
    }
    setModal(null);
  };
  // Dupliquer une tâche : rouvre la modale de création avec les mêmes valeurs (sans id)
  const dupTask=f=>{
    const {id,_hourTouched,...rest}=f;
    // On garde l'heure choisie : c'est utile en dupliquant. L'utilisateur changera s'il veut.
    setModal({prefill:{...rest,id:null,st:'todo',rs:null,re:null,_hourTouched:true}});
  };

  // Chips cliquables (édition si admin)
  const sc=(t)=>{
    const tt=TT[t.type]||{e:'❓',c:'#999',bg:'#eee',l:t.type||'Type inconnu'};const b=biens.find(x=>x.id===t.bid);
    const finTot=t.sh*60+t.sm+t.dm;
    const finH=Math.floor(finTot/60),finM=finTot%60;
    const es=effSt(t,als);
    const startMin=t.sh*60+(t.sm||0);
    const chevaucheChaleur=startMin<17*60&&finTot>12*60;
    const isHeureChaude=(caniNiveau==='orange'||caniNiveau==='rouge')&&chevaucheChaleur&&es!=='cancelled'&&es!=='done';
    return(<div key={t.id} onClick={()=>isAdmin&&setModal({prefill:{...t,assigneeType:'staff'}})} className={`chip chi ${es==='done'?'done':''} ${es==='progress'?'progress':''}`} style={{borderLeft:`3px solid ${tt.c}`,position:'relative',cursor:'pointer',...(es==='cancelled'?{opacity:0.45,textDecoration:'line-through'}:{})}}>
      {t.prio==='urgent'&&es!=='cancelled'&&<div style={{background:'var(--dg)',color:'white',fontSize:9,fontWeight:700,textAlign:'center',padding:'1px 0',borderRadius:'3px 3px 0 0',margin:'-4px -6px 3px -6px',letterSpacing:'.05em'}}>⚡ URGENT</div>}
      {t.st==='a_verifier'&&<div style={{background:'#b91c1c',color:'white',fontSize:9,fontWeight:700,textAlign:'center',padding:'1px 0',borderRadius:'3px 3px 0 0',margin:'-4px -6px 3px -6px',letterSpacing:'.05em'}}>⚠️ À VÉRIFIER</div>}
      {es==='cancelled'&&<div style={{background:'#6b7280',color:'white',fontSize:9,fontWeight:700,textAlign:'center',padding:'1px 0',borderRadius:'3px 3px 0 0',margin:'-4px -6px 3px -6px',letterSpacing:'.05em'}}>ANNULÉ</div>}
      <div style={{fontWeight:700,fontSize:11,display:'flex',alignItems:'center',gap:4,flexWrap:'wrap'}}>
        {tt.e} {tt?.noBid?tt.l:b?.name}
        {t.groupeBinome&&(()=>{
          const partenaire=st.find(x=>x.groupeBinome===t.groupeBinome&&x.id!==t.id);
          const staffBinome=partenaire?staff.find(s=>s.id===partenaire.sid):null;
          const nom=staffBinome?fnameShort(staffBinome):null; // forme courte "Prénom N." pour le badge, gain de place
          const nomComplet=staffBinome?fname(staffBinome):null; // nom complet réservé à l'infobulle
          return<span title={nomComplet?`Binôme avec ${nomComplet}`:'Binôme'} style={{background:'#EEEDFE',color:'#3C3489',fontWeight:600,fontSize:9,padding:'1px 6px',borderRadius:999,display:'inline-flex',alignItems:'center',gap:2}}>🤝{nom?` ${nom}`:''}</span>;
        })()}
        {t.alerte&&<span title={t.alerte} style={{background:'#fff4e0',color:'#8a5a00',fontWeight:600,fontSize:9,padding:'1px 6px',borderRadius:999}}>⚠️ Alerte</span>}
      </div>
      {tt?.noBid&&t.notes&&<div style={{fontSize:10,opacity:.85,fontStyle:'italic',marginTop:1}}>{t.notes}</div>}
      {t.type==='checkin'&&t.rdv
        ?<div style={{fontSize:10,opacity:.95}}><span style={{background:'#4a9eff',color:'white',fontWeight:700,fontSize:9,padding:'0 4px',borderRadius:3,marginRight:4,letterSpacing:'.03em'}}>📌 RDV</span><span style={{fontWeight:700,color:'#1e6fd6'}}>{String(t.sh).padStart(2,'0')}:{String(t.sm).padStart(2,'0')}</span> → {String(finH).padStart(2,'0')}:{String(finM).padStart(2,'0')}</div>
        :<div style={{fontSize:10,opacity:.8}}>{String(t.sh).padStart(2,'0')}:{String(t.sm).padStart(2,'0')} → {String(finH).padStart(2,'0')}:{String(finM).padStart(2,'0')}</div>}
      {t.type==='intervention'&&t.natureIntervention&&<div style={{fontSize:10,fontStyle:'italic',opacity:.85,marginTop:1}}>🔧 {t.natureIntervention}</div>}
      {es==='progress'&&<div style={{position:'absolute',top:3,right:3,fontSize:9,fontWeight:700,color:'#b8730c',background:'rgba(255,255,255,.85)',padding:'1px 5px',borderRadius:4}}>▶ en cours</div>}
      {isAdmin&&<button onClick={e=>{e.stopPropagation();dupTask({...t,assigneeType:'staff'});}} title="Dupliquer cette tâche" className="chip-dup" style={{position:'absolute',top:3,right:3,width:18,height:18,borderRadius:4,background:'rgba(255,255,255,.85)',border:'1px solid rgba(0,0,0,.1)',cursor:'pointer',fontSize:10,padding:0,display:'flex',alignItems:'center',justifyContent:'center',opacity:0,transition:'opacity .15s'}}>📋</button>}
      {isHeureChaude&&<div style={{background:'#FFC107',color:'#1a1a1a',fontSize:9,fontWeight:700,textAlign:'center',padding:'1px 0',borderRadius:'0 0 3px 3px',margin:'4px -6px -4px -6px',letterSpacing:'.05em'}}>🌡️ heure chaude</div>}
    </div>);
  };
  const pc=(j)=>{
    const b=biens.find(x=>x.id===j.bid);
    return(<div key={j.id} onClick={()=>isAdmin&&setModal({prefill:{...j,assigneeType:'provider'}})} className={`chip ${j.pid?'che':'chu'} ${j.st==='done'?'done':''}`} style={{position:'relative',cursor:'pointer'}}>
      {j.prio==='urgent'&&<div style={{background:'var(--dg)',color:'white',fontSize:9,fontWeight:700,textAlign:'center',padding:'1px 0',borderRadius:'3px 3px 0 0',margin:'-4px -6px 3px -6px',letterSpacing:'.05em'}}>⚡ URGENT</div>}
      <div style={{fontWeight:700,fontSize:11}}>🏠 {b?.name}</div>
      <div style={{fontSize:10,opacity:.8}}>{j.af} → {j.db}</div>
      {isAdmin&&<button onClick={e=>{e.stopPropagation();dupTask({...j,assigneeType:'provider'});}} title="Dupliquer cette tâche" className="chip-dup" style={{position:'absolute',top:3,right:3,width:18,height:18,borderRadius:4,background:'rgba(255,255,255,.85)',border:'1px solid rgba(0,0,0,.1)',cursor:'pointer',fontSize:10,padding:0,display:'flex',alignItems:'center',justifyContent:'center',opacity:0,transition:'opacity .15s'}}>📋</button>}
    </div>);
  };

  // Cellule cliquable (nouvelle tâche en admin)
  const Cell=({className,children,onAdd})=>(
    <div className={className} style={{position:'relative'}}
      onClick={e=>{if(e.target===e.currentTarget&&onAdd)onAdd();}}
    >
      {children}
      {isAdmin&&onAdd&&<button onClick={e=>{e.stopPropagation();onAdd();}} style={{position:'absolute',bottom:4,right:4,width:18,height:18,borderRadius:'50%',background:'rgba(255,134,23,0.15)',border:'1px solid rgba(255,134,23,0.3)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,cursor:'pointer',opacity:0,transition:'opacity .15s',color:'var(--or)',fontWeight:700}}
        className="cell-add-btn">+</button>}
    </div>
  );

  // Calcul coûts/CA/marge par jour (admin uniquement)
  const dayStats=useMemo(()=>{
    if(!isAdmin) return {};
    // Sets des IDs valides : on ne compte que les tâches affectées à des personnes qui existent encore
    const validStaffIds=new Set(staff.map(s=>s.id));
    const validProvIds=new Set(prov.map(p=>p.id));
    return Object.fromEntries(days.map(d=>{
      const dI=iD(d);
      // Coût MO salariés : durée planifiée × taux horaire chargé (uniquement tâches affectées à un salarié existant)
      const coutSalaries=st.filter(t=>t.date===dI&&t.sid&&validStaffIds.has(t.sid)).reduce((sum,t)=>{
        const s=staff.find(x=>x.id===t.sid);
        // Étape 1B : taux horaire du contrat en vigueur LE JOUR DE LA TÂCHE,
        // pas le taux actuel — sinon une revalorisation récente réécrirait le
        // coût de toutes les journées passées déjà affichées.
        const tx=Number(contratActif(s,dI)?.hr)||0;
        return sum+(t.dm/60)*tx*1.45;
      },0);
      // Coût prestataires : durée planifiée × tarif (uniquement missions affectées à un prestataire existant)
      const coutPrest=pj.filter(j=>j.date===dI&&j.pid&&validProvIds.has(j.pid)).reduce((sum,j)=>{
        const p=prov.find(x=>x.id===j.pid);
        if(!p) return sum;
        if(p.tarifType==='forfait'){
          const type=j.type||'full';
          if(type==='checkin') return sum+(p.tarifCheckin||0);
          if(type==='checkout') return sum+(p.tarifCheckout||0);
          return sum+((p.forfaits||{})[j.bid]||0);
        }
        return sum+(j.dm||90)/60*(p.tauxHoraire||0);
      },0);
      const cout=coutSalaries+coutPrest;
      // CA : calculé par le helper caTask pour chaque tâche
      // (tarif selon type + tarif urgent + majoration +25% + extras facturés via billTotal du rapport soumis)
      // Les tâches non affectées (et à risque) ou orphelines (salarié supprimé) ne comptent pas dans le CA
      const ca=st.filter(t=>t.date===dI&&t.sid&&validStaffIds.has(t.sid)).reduce((sum,t)=>{
        const b=biens.find(x=>x.id===t.bid);
        return sum+caTask(t,b,rep);
      },0);
      const marge=ca-cout;
      const margePct=ca>0?Math.round((marge/ca)*100):null;
      return[dI,{cout,ca,marge,margePct,hasTasks:st.some(t=>t.date===dI)||pj.some(j=>j.date===dI)}];
    }));
  },[days,st,pj,staff,prov,biens,isAdmin,rep]);

  const wl=`${ws.toLocaleDateString('fr-FR',{day:'numeric',month:'short'})} → ${aD(ws,6).toLocaleDateString('fr-FR',{day:'numeric',month:'short',year:'numeric'})}`;

  return(<div>
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
      <button onClick={()=>setWs(aD(ws,-7))} className="wn">‹</button>
      <button onClick={()=>setWs(aD(ws,7))} className="wn">›</button>
      <button onClick={()=>setWs(gMon(new Date()))} className="btn bgh sm">Auj.</button>
      <span className="D" style={{fontSize:16,marginLeft:6}}>{wl}</span>
      {isAdmin&&<button onClick={()=>openNew({date:today()})} className="btn bor sm" style={{marginLeft:'auto'}}><Ic n="plus"/>Nouvelle tâche</button>}
      {isAdmin&&totUn>0&&<span style={{background:'#fde0e0',color:'#b03030',fontSize:12,fontWeight:700,padding:'3px 10px',borderRadius:999}}>{totUn} non affecté{totUn>1?'s':''}</span>}
      {isAdmin&&totRisk>0&&<span style={{background:'#fff4d6',color:'#7a5a00',fontSize:12,fontWeight:700,padding:'3px 10px',borderRadius:999,border:'1px solid #e5c977'}}>⚠️ {totRisk} à risque</span>}
      {isAdmin&&totUrg>0&&<span style={{background:'var(--dg)',color:'white',fontSize:12,fontWeight:700,padding:'3px 10px',borderRadius:999}}>⚡ {totUrg} urgent{totUrg>1?'s':''}</span>}
      {isAdmin&&totOrph>0&&<button onClick={()=>setShowOrph(true)} style={{background:'#3a3530',color:'white',fontSize:12,fontWeight:700,padding:'3px 10px',borderRadius:999,border:'none',cursor:'pointer'}}>🔍 {totOrph} orpheline{totOrph>1?'s':''}</button>}
    </div>
    {isAdmin&&<div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
      <span style={{fontSize:11,color:'var(--si)',fontWeight:600}}>🔍 Filtrer :</span>
      <select value={filtCid} onChange={e=>{setFiltCid(e.target.value);setFiltBid('');}} style={{maxWidth:220}}>
        <option value="">Tous les propriétaires</option>
        {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select value={filtBid} onChange={e=>setFiltBid(e.target.value)} style={{maxWidth:220}}>
        <option value="">{filtCid?'Tous ses biens':'Tous les biens'}</option>
        {biensFiltre.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      {filtreActif&&<label style={{display:'flex',alignItems:'center',gap:4,fontSize:11,color:'var(--si)',cursor:'pointer'}}>
        <input type="checkbox" checked={hideEmpty} onChange={e=>setHideEmpty(e.target.checked)}/>
        Masquer les lignes sans tâche
      </label>}
      {filtreActif&&<button onClick={()=>{setFiltCid('');setFiltBid('');setHideEmpty(false);}} className="btn bor sm">✕ Réinitialiser</button>}
    </div>}
    <style>{`.cell-add-btn{opacity:0!important;}.pc:hover .cell-add-btn{opacity:1!important;}.chip-dup{opacity:0!important;}.chip:hover .chip-dup{opacity:1!important;}`}</style>
    <div className="card" style={{overflow:'hidden'}}><div style={{overflowX:'auto'}}>
      <div className="pg">
        <div className="ph" style={{textAlign:'left'}}><span className="D" style={{fontSize:13}}>Personne / Jour</span></div>
        {days.map((d,i)=>{const isT=iD(d)===todayS;return(<div key={i} className={`ph ${isT?'td':''}`}><div style={{fontSize:9,opacity:.7,textTransform:'uppercase'}}>{d.toLocaleDateString('fr-FR',{weekday:'long'})}</div><div className="D" style={{fontSize:18}}>{d.getDate()}</div><div style={{fontSize:10,opacity:.7}}>{d.toLocaleDateString('fr-FR',{month:'short'})}</div></div>);})}
        {(isAdmin||scopeS)&&vSAff.length>0&&(<Fragment>
          <div className="psh int">{scopeS?'Mon planning':'Équipe interne · '+vSAff.length}</div>
          {vSAff.map(s=>{const mSem=minutesSemaine(s.id);return(<Fragment key={s.id}>
            {(()=>{
              // Calcul déficit mensuel pour la pastille admin
              let deficitH=0;
              if(isAdmin&&tc&&tc.length>0){
                const today_=new Date();
                const y=today_.getFullYear();const m=today_.getMonth();
                const firstD=`${y}-${String(m+1).padStart(2,'0')}-01`;
                const todayISO=today_.toISOString().slice(0,10);
                // Jours écoulés du mois → rhHContratJours (utils.js, source unique) :
                // plafonne correctement à wd jours contractuels/semaine. Avant ce
                // correctif, ce calcul inline ne plafonnait pas (même bug que la
                // copie morte d'AdminUI.jsx) — un salarié à temps partiel (wd<5)
                // se voyait crédité d'heures contractuelles sur tous les jours
                // ouvrés du mois, gonflant artificiellement son déficit affiché.
                const joursEcoules=[];let d_=new Date(firstD+'T00:00:00');
                while(d_.toISOString().slice(0,10)<=todayISO){joursEcoules.push(d_.toISOString().slice(0,10));d_.setDate(d_.getDate()+1);}
                const hContrat=rhHContratJours(s,joursEcoules);
                // Heures effectives du mois
                const hEff=(tc||[]).filter(x=>x.sid===s.id&&x.date>=firstD&&x.date<=todayISO).reduce((acc,x)=>{
                  if(!x.sw||!x.ew)return acc;
                  const ms=Math.max(0,new Date(x.ew.ts)-new Date(x.sw.ts));
                  return acc+ms/3600000;
                },0);
                // Absences approuvées du mois — Étape 1B : 4ᵉ copie inline du
                // calcul supprimée au profit du helper partagé rhHAbsJours
                // (utils.js). L'Étape 0 avait unifié rhHContratJours mais
                // laissé celle-ci, qui DIVERGEAIT : elle ignorait les types
                // 'demi' et 'recup', donc sous-estimait les absences et
                // gonflait d'autant le déficit affiché sur le planning.
                const{hAbs}=rhHAbsJours(s,joursEcoules,leave);
                deficitH=Math.max(0,hContrat-hEff-hAbs);
              }
              const fhDef=h=>`${Math.floor(h)}h${Math.round((h%1)*60).toString().padStart(2,'0')}`;
              return(<div className="prl">
                <div style={{display:'flex',alignItems:'center',gap:5}}>
                  <div className="D" style={{fontSize:12,color:'var(--mn)'}}>{fname(s)}</div>
                  {isAdmin&&deficitH>0.5&&<div title={`Déficit : ${fhDef(deficitH)} ce mois`} style={{background:'var(--dg)',color:'#fff',fontSize:9,fontWeight:700,borderRadius:999,padding:'1px 5px',lineHeight:1.5,cursor:'default',flexShrink:0}}>-{fhDef(deficitH)}</div>}
                </div>
                <div style={{fontSize:10,color:'var(--si)'}}>{s.role}</div>
                {isAdmin&&mSem>0&&<div style={{fontSize:10,fontWeight:700,color:'var(--mn)',marginTop:2}}>📊 {fhm(mSem)} cette sem.</div>}
              </div>);
            })()}
            {days.map((d,i)=>{const isT=iD(d)===todayS,isW=d.getDay()===0||d.getDay()===6,dI=iD(d);const absJour=leave.find(l=>l.sid===s.id&&l.st==='approved'&&dI>=l.sd&&dI<=l.ed);const onL=!!absJour;const isRepos=absJour?.type==='repos';const mJ=minutesJour(s.id,d);
              // Jour habituellement non travaillé pour ce salarié
              const dow=d.getDay();
              const isJI=!onL&&(s.joursIndispo||[]).includes(dow);
              const hasTacheExcep=isJI&&stFor(s.id,d).length>0; // tâche déjà affectée ce jour malgré indispo
              const tp=!onL&&dI>=todayS?tpj(s)(d):null;
              const handleAdd=isAdmin&&!onL?()=>{
                if(isJI){
                  if(window.confirm(`${fname(s)} ne travaille habituellement pas ce jour.
Affecter une tâche exceptionnellement ?`)){
                    openNew({sid:s.id,date:dI,assigneeType:'staff'});
                  }
                }else{
                  openNew({sid:s.id,date:dI,assigneeType:'staff'});
                }
              }:null;
              return(<Cell key={i} className={`pc ${isW?'we':''} ${isT?'td':''} ${onL?'un':''} ${isJI&&!hasTacheExcep?'ji':''}`} style={isJI&&hasTacheExcep?{background:'rgba(255,180,0,0.08)'}:{}} onAdd={handleAdd}>
                {onL&&(isRepos
                  ?<div style={{fontSize:9,fontWeight:700,color:'#6d28d9',textAlign:'center',paddingTop:14,textTransform:'uppercase'}}>🔄 Repos</div>
                  :<div style={{fontSize:9,fontWeight:700,color:'var(--si)',textAlign:'center',paddingTop:14,textTransform:'uppercase',opacity:.6}}>{absJour?.type==='maladie'&&(isAdmin||(scopeS&&scopeS===s.id))?(absJour.malType==='at'?'⚠️ AT':'🏥 Arrêt'):'Congé'}</div>)}
                {isJI&&!onL&&!hasTacheExcep&&<div style={{fontSize:9,color:'var(--si)',textAlign:'center',paddingTop:14,opacity:.5,fontStyle:'italic'}}>—</div>}
                {isJI&&hasTacheExcep&&<div style={{position:'absolute',top:2,right:2,fontSize:9,color:'#c05a00',fontWeight:700,background:'rgba(255,180,0,0.2)',borderRadius:4,padding:'1px 4px'}} title="Shift exceptionnel">exc.</div>}
                {stFor(s.id,d).map(sc)}
                {isAdmin&&mJ>0&&!onL&&<div style={{position:'absolute',bottom:2,left:4,fontSize:9,color:'var(--si)',fontWeight:600,pointerEvents:'none'}}>{fhm(mJ)}</div>}
                {tp&&(isAdmin
                  ?<div title={`${fhm(tp.restantMin)} restant sur le quota du jour${tp.pauseCouverte?' — trou existant compté comme pause':' — pause d’1h ajoutée (aucun trou ≥30 min entre les tâches)'}`} style={{position:'absolute',bottom:2,right:4,fontSize:9,color:'#0d7a6c',fontWeight:700,background:'#e6fbf7',padding:'1px 5px',borderRadius:999,pointerEvents:'none'}}>🕓 jusqu'à {tp.finLabel}</div>
                  :<div title={`${fhm(tp.restantMin)} restant sur le quota du jour${tp.pauseCouverte?' — trou existant compté comme pause':' — pause d’1h ajoutée (aucun trou ≥30 min entre les tâches)'}`} style={{marginTop:4,background:'#4a9eff',color:'#fff',fontSize:10,fontWeight:700,textAlign:'center',padding:'3px 2px',borderRadius:6,letterSpacing:'.02em'}}>⏱️ Peut travailler jusqu'à {tp.finLabel} *</div>)}
              </Cell>);})}
          </Fragment>);})}
        </Fragment>)}
        {(isAdmin||scopeP)&&vPAff.length>0&&(<Fragment>
          <div className="psh ext">{scopeP?'Mon planning':'Prestataires · '+vPAff.length}</div>
          {vPAff.map(p=>(<Fragment key={p.id}>
            <div className="prl"><div className="D" style={{fontSize:12,color:'var(--or)'}}>{p.name.split(' ')[0]}</div><div style={{fontSize:10,color:'var(--si)'}}>{p.type}</div></div>
            {days.map((d,i)=>{const isT=iD(d)===todayS,isW=d.getDay()===0||d.getDay()===6,dI=iD(d);const isU=unavail.some(u=>u.pid===p.id&&dI>=u.sd&&dI<=u.ed);
              return(<Cell key={i} className={`pc ${isW?'we':''} ${isT?'td':''} ${isU?'un':''}`} onAdd={isAdmin&&!isU?()=>openNew({pid:p.id,date:dI,assigneeType:'provider'}):null}>
                {isU&&<div style={{fontSize:9,fontWeight:700,color:'var(--si)',textAlign:'center',paddingTop:14,textTransform:'uppercase',opacity:.6}}>Indispo</div>}
                {pvFor(p.id,d).map(pc)}
              </Cell>);})}
          </Fragment>))}
        </Fragment>)}
        {isAdmin&&(<Fragment>
          <div className="psh uns">À affecter</div>
          <div className="prl" style={{background:'#fff0f0'}}><div className="D" style={{fontSize:12,color:'var(--pk)'}}>Non affecté</div></div>
          {days.map((d,i)=>{const isT=iD(d)===todayS,isW=d.getDay()===0||d.getDay()===6,dI=iD(d);
            return(<Cell key={i} className={`pc ${isW?'we':''} ${isT?'td':''}`} onAdd={()=>openNew({date:dI,sid:null,assigneeType:'staff'})}>
              {unFor(d).map(item=>item._src==='st'?sc(item):pc(item))}
            </Cell>);})}
          <div className="prl" style={{background:'#fff8e0'}}><div className="D" style={{fontSize:12,color:'#7a5a00'}}>⚠️ À risque</div><div style={{fontSize:10,color:'var(--si)'}}>Couvert ailleurs</div></div>
          {days.map((d,i)=>{const isT=iD(d)===todayS,isW=d.getDay()===0||d.getDay()===6,dI=iD(d);
            return(<Cell key={i} className={`pc ${isW?'we':''} ${isT?'td':''}`}>
              {riskFor(d).map(item=>item._src==='st'?sc(item):pc(item))}
            </Cell>);})}
        </Fragment>)}

        {/* ── Lignes Arrivées / Départs ── */}
        {isAdmin&&(<Fragment>
          {/* Arrivées */}
          <div className="prl" style={{background:'#dbeeff',justifyContent:'center',flexDirection:'column'}}>
            <div style={{fontSize:9,fontWeight:700,color:'#1e5fa8',textTransform:'uppercase',letterSpacing:'.08em'}}>🔑 Arrivées</div>
          </div>
          {days.map((d,i)=>{
            const dI=iD(d),isW=d.getDay()===0||d.getDay()===6,isT=dI===todayS;
            const items=adForDay('arr',dI);
            return(<div key={i} onClick={()=>openAdModal('arr',dI)} style={{padding:'4px 6px',borderRight:'1px solid var(--li)',borderBottom:'1px solid var(--li)',background:isW?'#f0ede8':isT?'rgba(255,134,23,0.05)':'#f5f9ff',minHeight:28,cursor:'pointer',display:'flex',flexDirection:'column',gap:2}}>
              {items.length>0?items.map((it,j)=>(
                <div key={j} style={{fontSize:10,fontWeight:600,color:'#1e5fa8',display:'flex',alignItems:'center',gap:3}}>
                  {it.src==='manual'?<span style={{opacity:.6}}>✏️</span>:<span style={{opacity:.5}}>🔒</span>}
                  <span>{it.name}</span><span style={{opacity:.6,fontWeight:400}}>{it.heure}</span>
                </div>
              )):<div style={{fontSize:10,color:'#c8d8f0',textAlign:'center',paddingTop:4}}>+</div>}
            </div>);})}

          {/* Départs */}
          <div className="prl" style={{background:'#f3e8ff',justifyContent:'center',flexDirection:'column'}}>
            <div style={{fontSize:9,fontWeight:700,color:'#6b21a8',textTransform:'uppercase',letterSpacing:'.08em'}}>🚪 Départs</div>
          </div>
          {days.map((d,i)=>{
            const dI=iD(d),isW=d.getDay()===0||d.getDay()===6,isT=dI===todayS;
            const items=adForDay('dep',dI);
            return(<div key={i} onClick={()=>openAdModal('dep',dI)} style={{padding:'4px 6px',borderRight:'1px solid var(--li)',borderBottom:'1px solid var(--li)',background:isW?'#f0ede8':isT?'rgba(255,134,23,0.05)':'#fdf5ff',minHeight:28,cursor:'pointer',display:'flex',flexDirection:'column',gap:2}}>
              {items.length>0?items.map((it,j)=>(
                <div key={j} style={{fontSize:10,fontWeight:600,color:'#6b21a8',display:'flex',alignItems:'center',gap:3}}>
                  {it.src==='manual'?<span style={{opacity:.6}}>✏️</span>:<span style={{opacity:.5}}>🔒</span>}
                  <span>{it.name}</span><span style={{opacity:.6,fontWeight:400}}>{it.heure}</span>
                </div>
              )):<div style={{fontSize:10,color:'#ddd0f0',textAlign:'center',paddingTop:4}}>+</div>}
            </div>);})}
        </Fragment>)}

        {/* ── Lignes économiques (admin uniquement) ── */}
        {isAdmin&&(()=>{
          // Totaux semaine
          const totW={cout:0,ca:0,marge:0};
          days.forEach(d=>{const s=dayStats[iD(d)];if(s?.hasTasks){totW.cout+=s.cout;totW.ca+=s.ca;totW.marge+=s.marge;}});
          const margePctW=totW.ca>0?Math.round((totW.marge/totW.ca)*100):null;
          const okW=margePctW!=null&&margePctW>=30;
          const warnW=margePctW!=null&&margePctW>=0&&margePctW<30;
          return(<Fragment>
          {/* Coût MO */}
          <div className="prl" style={{background:'#1e1a17',justifyContent:'center',flexDirection:'column'}}>
            <div style={{fontSize:9,fontWeight:700,color:'rgba(255,255,255,.5)',textTransform:'uppercase',letterSpacing:'.08em'}}>Coût MO</div>
            <div style={{fontSize:11,fontWeight:700,color:'#ff8e8e',marginTop:2}}>{feur(totW.cout)} / sem</div>
          </div>
          {days.map((d,i)=>{
            const dI=iD(d);const s=dayStats[dI];const isW=d.getDay()===0||d.getDay()===6,isT=dI===todayS;
            return(<div key={i} style={{padding:'4px 6px',borderRight:'1px solid var(--li)',borderBottom:'1px solid var(--li)',background:isW?'#f0ede8':isT?'rgba(255,134,23,0.05)':'#faf7f3',display:'flex',alignItems:'center',justifyContent:'center',minHeight:28}}>
              {s?.hasTasks?<div style={{fontSize:11,fontWeight:700,color:'var(--dg)',textAlign:'center'}}>{feur(s.cout)}</div>:<div style={{fontSize:10,color:'var(--li)'}}>—</div>}
            </div>);})}

          {/* CA */}
          <div className="prl" style={{background:'#1e1a17',justifyContent:'center',flexDirection:'column'}}>
            <div style={{fontSize:9,fontWeight:700,color:'rgba(255,255,255,.5)',textTransform:'uppercase',letterSpacing:'.08em'}}>CA</div>
            <div style={{fontSize:11,fontWeight:700,color:'#7eddae',marginTop:2}}>{feur(totW.ca)} / sem</div>
          </div>
          {days.map((d,i)=>{
            const dI=iD(d);const s=dayStats[dI];const isW=d.getDay()===0||d.getDay()===6,isT=dI===todayS;
            return(<div key={i} style={{padding:'4px 6px',borderRight:'1px solid var(--li)',borderBottom:'1px solid var(--li)',background:isW?'#f0ede8':isT?'rgba(255,134,23,0.05)':'#faf7f3',display:'flex',alignItems:'center',justifyContent:'center',minHeight:28}}>
              {s?.hasTasks?<div style={{fontSize:11,fontWeight:700,color:'var(--ok)',textAlign:'center'}}>{feur(s.ca)}</div>:<div style={{fontSize:10,color:'var(--li)'}}>—</div>}
            </div>);})}

          {/* Marge */}
          <div className="prl" style={{background:'#1e1a17',justifyContent:'center',flexDirection:'column'}}>
            <div style={{fontSize:9,fontWeight:700,color:'rgba(255,255,255,.5)',textTransform:'uppercase',letterSpacing:'.08em'}}>Marge</div>
            <div style={{fontSize:11,fontWeight:800,color:okW?'#7eddae':warnW?'#ffd57e':'#ff8e8e',marginTop:2}}>{feur(totW.marge)}{margePctW!=null&&<span style={{fontSize:9,marginLeft:4,fontWeight:700}}>({margePctW}%)</span>}</div>
          </div>
          {days.map((d,i)=>{
            const dI=iD(d);const s=dayStats[dI];const isW=d.getDay()===0||d.getDay()===6,isT=dI===todayS;
            const ok=s?.margePct!=null&&s.margePct>=30;
            const warn=s?.margePct!=null&&s.margePct>=0&&s.margePct<30;
            return(<div key={i} style={{padding:'4px 6px',borderRight:'1px solid var(--li)',background:isW?'#f0ede8':isT?'rgba(255,134,23,0.05)':'#faf7f3',display:'flex',alignItems:'center',justifyContent:'center',minHeight:28}}>
              {s?.hasTasks?<div style={{textAlign:'center'}}>
                <div style={{fontSize:11,fontWeight:800,color:ok?'var(--ok)':warn?'var(--ol)':'var(--dg)'}}>{feur(s.marge)}</div>
                {s.margePct!=null&&<div style={{fontSize:9,fontWeight:700,color:ok?'var(--ok)':warn?'var(--ol)':'var(--dg)'}}>{s.margePct}%</div>}
              </div>:<div style={{fontSize:10,color:'var(--li)'}}>—</div>}
            </div>);})}
        </Fragment>);})()}
      </div>
    </div></div>
    {anyTpThisWeek&&<div style={{fontSize:11,color:'var(--si)',marginTop:6,paddingLeft:2}}>* Le planning peut encore être modifié jusqu'à cette heure — restez attentif·ve.</div>}
    {adModal&&<M onC={()=>setAdModal(null)} title={adModal.type==='arr'?'🔑 Arrivées du '+new Date(adModal.dateStr+'T12:00:00').toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'}):'🚪 Départs du '+new Date(adModal.dateStr+'T12:00:00').toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'})} col={adModal.type==='arr'?'#4a9eff':'#a855f7'}
      ch={<div style={{display:'flex',flexDirection:'column',gap:12}}>
        <div style={{fontSize:12,color:'var(--si)',background:'var(--bg)',borderRadius:8,padding:'8px 11px'}}>
          {adModal.type==='arr'?'🔒 Les arrivées issues d\'une tâche check-in ou de l\'iCal Eviivo sont affichées automatiquement et ne peuvent pas être modifiées ici.':'🔒 Les départs issus d\'une tâche check-out ou de l\'iCal Eviivo sont affichés automatiquement et ne peuvent pas être modifiés ici.'}
          <br/>✏️ Ajoutez ici les arrivées/départs non couverts (ex. clients Delphine Thomas).
        </div>
        {adModal.entries.map((e,i)=>(
          <div key={i} style={{display:'flex',gap:8,alignItems:'center'}}>
            <select value={e.bid} onChange={ev=>{const next=[...adModal.entries];next[i]={...next[i],bid:ev.target.value};setAdModal(x=>({...x,entries:next}));}}
              style={{flex:1,padding:'6px 8px',borderRadius:6,border:'1px solid var(--li)',fontSize:13}}>
              <option value="">— Choisir un appartement</option>
              {biens.filter(b=>!b.bienAnnexe).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <input type="time" value={e.heure||''} onChange={ev=>{const next=[...adModal.entries];next[i]={...next[i],heure:ev.target.value};setAdModal(x=>({...x,entries:next}));}}
              style={{width:90,padding:'6px 8px',borderRadius:6,border:'1px solid var(--li)',fontSize:13}}/>
            {adModal.type==='dep'&&<label style={{display:'flex',alignItems:'center',gap:4,fontSize:11,color:'var(--si)',whiteSpace:'nowrap',cursor:'pointer'}}>
              <input type="checkbox" checked={e.creerMenage!==false} onChange={ev=>{const next=[...adModal.entries];next[i]={...next[i],creerMenage:ev.target.checked};setAdModal(x=>({...x,entries:next}));}}/>
              + ménage
            </label>}
            {adModal.type==='arr'&&(biens.find(b=>b.id===e.bid)?.prestations||[]).includes('checkin')&&<label style={{display:'flex',alignItems:'center',gap:4,fontSize:11,color:'var(--si)',whiteSpace:'nowrap',cursor:'pointer'}}>
              <input type="checkbox" checked={e.creerCheckin!==false} onChange={ev=>{const next=[...adModal.entries];next[i]={...next[i],creerCheckin:ev.target.checked};setAdModal(x=>({...x,entries:next}));}}/>
              + check-in
            </label>}
            <button onClick={()=>setAdModal(x=>({...x,entries:x.entries.filter((_,j)=>j!==i)}))}
              style={{padding:'4px 8px',borderRadius:6,border:'1px solid var(--li)',background:'#fee2e2',color:'var(--dg)',cursor:'pointer',fontSize:13}}>✕</button>
          </div>
        ))}
        <button onClick={()=>setAdModal(x=>({...x,entries:[...x.entries,{bid:'',heure:x.type==='arr'?'16:00':'10:00',creerMenage:x.type==='dep',creerCheckin:x.type==='arr'}]}))}
          className="btn bgh sm" style={{alignSelf:'flex-start'}}>+ Ajouter un appartement</button>
        {adModal.type==='dep'&&<div style={{fontSize:11,color:'var(--si)'}}>+ ménage : crée automatiquement la tâche ménage associée (non affectée, aucun doublon si une tâche existe déjà ce jour-là pour ce bien).</div>}
        {adModal.type==='arr'&&<div style={{fontSize:11,color:'var(--si)'}}>+ check-in : crée automatiquement la tâche check-in (non affectée) pour les appartements dont on gère l'accueil. Aucun doublon si une tâche check-in existe déjà ce jour-là (y compris via l'iCal Eviivo).</div>}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
          <button onClick={()=>setAdModal(null)} className="btn bgh sm">Annuler</button>
          <button onClick={saveAdModal} className="btn bor sm">✓ Enregistrer</button>
        </div>
      </div>}/>}

    {modal&&<M onC={()=>setModal(null)} title={modal.prefill?.id?'Modifier la tâche':'Nouvelle tâche'} col="var(--or)"
      ch={<TaskForm prefill={modal.prefill} biens={biens} staff={staff} prov={prov} st={st} pj={pj} clients={clients} onSave={saveTask} onC={()=>setModal(null)} onDelete={modal.prefill?.id?delTask:null} onDuplicate={modal.prefill?.id?dupTask:null}/>}/>}

    {showOrph&&<M onC={()=>setShowOrph(false)} title={`🔍 Tâches orphelines (${totOrph})`} col="#3a3530" wide={true}
      ch={<div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div style={{background:'var(--bg)',borderRadius:9,padding:'11px 14px',fontSize:12,color:'var(--si)',borderLeft:'3px solid var(--or)'}}>
          Ces tâches sont affectées à des salariés ou prestataires qui n'existent plus dans la base (probablement supprimés). Elles n'apparaissent pas dans le planning mais peuvent fausser les statistiques. Tu peux soit les <strong>désaffecter</strong> (elles repasseront en "Non affecté" pour que tu décides quoi en faire), soit les <strong>supprimer définitivement</strong>.
        </div>

        {totOrph===0&&<div className="empty">✅ Aucune tâche orpheline détectée. La base est saine.</div>}

        {totOrph>0&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button onClick={async()=>{
            if(!window.confirm(`Désaffecter les ${totOrph} tâche(s) orpheline(s) ?\n\nElles repasseront en "Non affecté" dans le planning.`))return;
            // Désaffecter côté st
            if(orphansSt.length>0){
              const cur=await supaGet('st7')||[];
              const orphIds=new Set(orphansSt.map(t=>t.id));
              const upd=cur.map(t=>orphIds.has(t.id)?{...t,sid:null}:t);
              await supaSet('st7',upd);
              setSt(upd);
            }
            // Désaffecter côté pj
            if(orphansPj.length>0){
              const cur=await supaGet('pj7')||[];
              const orphIds=new Set(orphansPj.map(j=>j.id));
              const upd=cur.map(j=>orphIds.has(j.id)?{...j,pid:null}:j);
              await supaSet('pj7',upd);
              setPj(upd);
            }
            setShowOrph(false);
          }} className="btn bor sm">↺ Tout désaffecter</button>

          <button onClick={async()=>{
            if(!window.confirm(`⚠️ Supprimer définitivement les ${totOrph} tâche(s) orpheline(s) ?\n\nCette action est irréversible.`))return;
            if(orphansSt.length>0){
              const cur=await supaGet('st7')||[];
              const orphIds=new Set(orphansSt.map(t=>t.id));
              const upd=cur.filter(t=>!orphIds.has(t.id));
              await supaSet('st7',upd);
              setSt(upd);
            }
            if(orphansPj.length>0){
              const cur=await supaGet('pj7')||[];
              const orphIds=new Set(orphansPj.map(j=>j.id));
              const upd=cur.filter(j=>!orphIds.has(j.id));
              await supaSet('pj7',upd);
              setPj(upd);
            }
            setShowOrph(false);
          }} className="btn bdg sm">🗑 Tout supprimer</button>
        </div>}

        {totOrph>0&&<div style={{maxHeight:'45vh',overflowY:'auto'}}>
          <table className="tbl">
            <thead><tr><th>Type</th><th>Bien</th><th>Date</th><th>Horaire</th><th>ID affecté (introuvable)</th></tr></thead>
            <tbody>
              {orphansSt.map(t=>{const b=biens.find(x=>x.id===t.bid);const tt=TT[t.type]||{};return(
                <tr key={t.id}>
                  <td><span style={{background:tt.bg||'var(--bg)',color:tt.c||'var(--si)',padding:'2px 7px',borderRadius:999,fontSize:11,fontWeight:600}}>{tt.e} {tt.l||t.type}</span></td>
                  <td><strong>{b?.name||'(bien supprimé)'}</strong></td>
                  <td>{fd(t.date)}</td>
                  <td>{String(t.sh).padStart(2,'0')}:{String(t.sm).padStart(2,'0')}</td>
                  <td style={{fontSize:10,color:'var(--si)',fontFamily:'monospace'}}>{t.sid}</td>
                </tr>);})}
              {orphansPj.map(j=>{const b=biens.find(x=>x.id===j.bid);return(
                <tr key={j.id}>
                  <td><span style={{background:'#ffe9d6',color:'var(--or)',padding:'2px 7px',borderRadius:999,fontSize:11,fontWeight:600}}>🏠 Mission</span></td>
                  <td><strong>{b?.name||'(bien supprimé)'}</strong></td>
                  <td>{fd(j.date)}</td>
                  <td>{j.af} → {j.db}</td>
                  <td style={{fontSize:10,color:'var(--si)',fontFamily:'monospace'}}>{j.pid}</td>
                </tr>);})}
            </tbody>
          </table>
        </div>}
      </div>}/>}
  </div>);
}

// ═══════════════════════════════════════════════════════════════════
// FIN SCRIPT 3 — PLANNING (TaskForm + WeekPlan)
// Découpage par frontière naturelle (option douce, zéro déplacement).
// ═══════════════════════════════════════════════════════════════════

export { TaskForm, WeekPlan, caTask }
